import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import dayjs from 'dayjs'
import {
  Box,
  Container,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  Button,
  TextField,
  Grid,
  Tabs,
  Tab,
  Divider,
  Chip,
  InputAdornment,
  Alert,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  MenuItem,
  useMediaQuery,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import { styled, createTheme, ThemeProvider } from '@mui/material/styles'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import NotesRoundedIcon from '@mui/icons-material/NotesRounded'
import {
  Edit,
  Save,
  Cancel,
  Download,
  Article,
  Person,
  Security,
  Bookmark,
  Business,
  Email,
  Phone,
  LocationOn,
  Star,
  Category,
  TrendingUp,
  Autorenew,
  CardMembership,
  Payments as PaymentsIcon,
  AddPhotoAlternate,
  DeleteOutline,
} from '@mui/icons-material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import apiService from '../services/ApiService'
import {
  COUNTRY_OPTIONS,
  PHONE_CODE_OPTIONS,
  MOBILE_NUMBER_LOCAL_MAX_DIGITS,
  getCountryPhoneCode,
  getStateOptions,
  normalizeLocalMobileNumber,
  resolveCountryCode,
  splitStoredMobileNumber,
  validateAndFormatMobileNumber,
} from '../utils/locationData'
import EnhancedBookmarkCard from './BookmarkCard'
import Pagination from './Pagination'
import ItemsPerPageSelector from './ItemsPerPageSelector'
import AnnotationsSection from './annotations/AnnotationsSection'
import SearchActivityTab from './SearchActivityTab'
import { exportBookmarksToCSV } from './bookmarks/bookmarkExportUtils'
import {
  createEmptyOtpDigits,
  extractBackendError,
  formatCountdown,
  maskEmailForDisplay,
  normalizeEmailValue,
} from '../utils/emailVerificationUtils'

const TAB_KEYS = {
  annotations: 0,
  bookmarks: 1,
  blogs: 2,
  searchActivity: 3,
  personal: 4,
  subscriptions: 5,
  payments: 6,
}

const TAB_QUERY = {
  0: 'annotations',
  1: 'bookmarks',
  2: 'blogs',
  3: 'search-activity',
  4: 'personal',
  5: 'subscriptions',
  6: 'payments',
}

const INITIAL_PROFILE_FORM = {
  name: '',
  username: '',
  email: '',
  company: '',
  location: '',
  bio: '',
  coverPhotoUrl: '',
  street: '',
  city: '',
  state: '',
  postalCode: '',
  country: '',
  profilePictureUrl: '',
}

const INITIAL_ANNOTATION_FILTERS = {
  category: 'all',
  priority: 'all',
  isImportant: 'all',
  tags: [],
}

const INITIAL_SEARCH_ACTIVITY_FILTERS = {
  type: 'ALL',
  country: '',
  law: '',
  dateFrom: '',
  dateTo: '',
}

const EMPTY_QUOTA_STATUS = {
  remainingLinkClicks: 0,
  monthlyLinkClicksLimit: 0,
  remainingDtaaViews: 0,
  monthlyDtaaViewsLimit: 0,
  creditsBalance: 0,
  activeSubscriptions: [],
  quotaResetDate: null,
}

const USER_BLOGS_PAGE_SIZE = 10
const BLOG_REVIEW_NOTICE_STORAGE_KEY = 'profile_blog_review_notice_state'
const COVER_ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp']
const COVER_MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024

const themeColors = {
  primary: '#232536',
  secondary: '#ffcf51',
  hover: '#3a3e5a',
}

const getMonotonicNowMs = () =>
  typeof window !== 'undefined' &&
  window.performance &&
  typeof window.performance.now === 'function'
    ? window.performance.now()
    : Date.now()

const toRemainingSeconds = (deadlineMs) =>
  Math.max(0, Math.ceil((Number(deadlineMs) - getMonotonicNowMs()) / 1000))

const getTabIndexFromQuery = (tab) => {
  if (tab === 'payments') return TAB_KEYS.payments
  if (tab === 'subscriptions') return TAB_KEYS.subscriptions
  if (tab === 'search-activity') return TAB_KEYS.searchActivity
  if (tab === 'blogs') return TAB_KEYS.blogs
  if (tab === 'bookmarks') return TAB_KEYS.bookmarks
  if (tab === 'personal') return TAB_KEYS.personal
  return TAB_KEYS.annotations
}

const convertArrayToDate = (dateInput) => {
  if (!dateInput) return null

  if (typeof dateInput === 'string') {
    const date = new Date(dateInput)
    return Number.isNaN(date.getTime()) ? null : date
  }

  if (!Array.isArray(dateInput)) {
    return null
  }

  const [year, month, day, hour = 0, minute = 0, second = 0, nanosecond = 0] =
    dateInput

  if (
    year === undefined ||
    month === undefined ||
    day === undefined ||
    Number.isNaN(year) ||
    Number.isNaN(month) ||
    Number.isNaN(day)
  ) {
    return null
  }

  const date = new Date(
    Date.UTC(
      year,
      month - 1,
      day,
      hour,
      minute,
      second,
      Math.floor(nanosecond / 1000000)
    )
  )

  return Number.isNaN(date.getTime()) ? null : date
}

const formatSubscriptionDate = (dateInput) => {
  const date = convertArrayToDate(dateInput)
  if (!date) return 'N/A'

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

const formatSubscriptionPrice = (price) => {
  if (!price) return 'AED 0'
  return new Intl.NumberFormat('en-AE', {
    style: 'currency',
    currency: 'AED',
    minimumFractionDigits: 0,
  }).format(price)
}

const formatSubscriptionDurationType = (durationType) => {
  const map = {
    DAILY: 'day',
    WEEKLY: 'week',
    MONTHLY: 'month',
    QUARTERLY: 'quarter',
    HALF_YEARLY: '6 months',
    ANNUAL: 'year',
  }
  return map[durationType] || 'period'
}

const getSubscriptionStatusChipColor = (status) => {
  if (status === 'Active') return 'success'
  if (status === 'Cancelled') return 'warning'
  if (status === 'Scheduled') return 'info'
  return 'default'
}

const getSubscriptionBorderColor = (status) => {
  if (status === 'Active') return '2px solid #43af76'
  if (status === 'Cancelled') return '1px solid #f3c779'
  if (status === 'Scheduled') return '1px solid #90caf9'
  return '1px solid #e0e0e0'
}

const formatBlogTimestamp = (dateInput) => {
  const parsedDate = convertArrayToDate(dateInput)
  if (!parsedDate) {
    return 'Date unavailable'
  }
  return parsedDate.toLocaleString([], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const getPaymentSortTimestamp = (payment) => {
  const completedAt = convertArrayToDate(payment?.completedAt)
  if (completedAt) {
    return completedAt.getTime()
  }
  const paymentDate = convertArrayToDate(payment?.paymentDate)
  return paymentDate ? paymentDate.getTime() : 0
}

const PAYMENT_PLAN_COUNTRY_PREFIX_MAP = Object.freeze({
  UAE: 'UAE',
  KSA: 'KSA',
  KUWAIT: 'Kuwait',
  QATAR: 'Qatar',
  OMAN: 'Oman',
  BAHRAIN: 'Bahrain',
})

const PAYMENT_COUNTRY_FILTER_ORDER = Object.freeze([
  'UAE',
  'KSA',
  'Kuwait',
  'Qatar',
  'Oman',
  'Bahrain',
])

const PAYMENT_PLAN_COUNTRY_PREFIX_ENTRIES = Object.entries(
  PAYMENT_PLAN_COUNTRY_PREFIX_MAP
)

const normalizePaymentPlanToken = (value) => {
  if (typeof value !== 'string' || !value.trim()) {
    return ''
  }
  return value.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_')
}

const resolvePaymentPlanMeta = (payment) => {
  if ((payment?.paymentType || '').toUpperCase() !== 'SUBSCRIPTION') {
    return { duration: 'other', scope: 'other', country: null }
  }

  const planToken =
    normalizePaymentPlanToken(payment?.planCode) ||
    normalizePaymentPlanToken(payment?.planName)

  const isRecurringPlan = payment?.isRecurring === true
  const isMonthlyPlan = isRecurringPlan || planToken.includes('MONTHLY')
  const isDailyPlan =
    planToken.includes('DAILY') ||
    planToken.includes('DAYPASS') ||
    planToken.includes('DAY_PASS')

  let scope = 'unknown'
  if (planToken.includes('GCC') || planToken.includes('ALL_COUNTRIES')) {
    scope = 'gcc'
  }

  let country = null
  if (scope !== 'gcc') {
    const countryMatch = PAYMENT_PLAN_COUNTRY_PREFIX_ENTRIES.find(
      ([countryPrefix, countryLabel]) =>
        planToken.startsWith(`${countryPrefix}_`) ||
        planToken.includes(`_${countryPrefix}_`) ||
        planToken.includes(countryLabel.toUpperCase())
    )

    if (countryMatch) {
      scope = 'country'
      country = countryMatch[1]
    }
  }

  return {
    duration: isMonthlyPlan ? 'monthly' : isDailyPlan ? 'daily' : 'unknown',
    scope,
    country,
  }
}

const getReviewTimestampToken = (reviewedAt) => {
  if (Array.isArray(reviewedAt)) {
    return reviewedAt.join('-')
  }
  if (typeof reviewedAt === 'string') {
    return reviewedAt
  }
  return ''
}

const buildReviewNoticeSignature = (blog) => (
  `${blog?.reviewStatus || 'DRAFT'}|${getReviewTimestampToken(blog?.reviewedAt)}|${blog?.reviewNotes || ''}`
)

const getProxiedUrl = (originalUrl) => {
  if (!originalUrl) return null
  return `${
    process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080'
  }/api/v1/profile/avatar?url=${encodeURIComponent(originalUrl)}`
}

const profileTheme = createTheme({
  palette: {
    primary: {
      main: themeColors.secondary,
      contrastText: themeColors.primary,
    },
    secondary: {
      main: themeColors.primary,
      contrastText: '#ffffff',
    },
    background: {
      default: '#f9fafb',
      paper: '#ffffff',
    },
    text: {
      primary: '#232536',
      secondary: '#4b5563',
    },
  },
  typography: {
    fontFamily: 'Inter, sans-serif',
    h1: {
      fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
      fontWeight: 700,
    },
    h2: {
      fontSize: 'clamp(1.25rem, 3.5vw, 2rem)',
      fontWeight: 600,
    },
    h3: {
      fontSize: 'clamp(1.125rem, 3vw, 1.75rem)',
      fontWeight: 600,
    },
    h4: {
      fontSize: 'clamp(1rem, 2.5vw, 1.5rem)',
      fontWeight: 600,
    },
    h5: {
      fontSize: 'clamp(0.875rem, 2vw, 1.25rem)',
      fontWeight: 600,
    },
    h6: {
      fontSize: 'clamp(0.75rem, 1.5vw, 1rem)',
      fontWeight: 600,
    },
    body1: {
      fontSize: 'clamp(0.75rem, 1.2vw, 1rem)',
    },
    body2: {
      fontSize: 'clamp(0.7rem, 1.1vw, 0.875rem)',
    },
    button: {
      fontSize: 'clamp(0.7rem, 1.1vw, 0.875rem)',
      fontWeight: 500,
    },
  },
})

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#232536',
  color: 'white',
  fontWeight: 'normal',
  transition: 'all 0.2s ease',
  minHeight: '40px',
  fontSize: 'clamp(0.7rem, 1.1vw, 0.875rem)',
  padding: theme.spacing(1, 2),
  [theme.breakpoints.down('sm')]: {
    minHeight: '36px',
    padding: theme.spacing(0.75, 1.5),
    fontSize: '0.75rem',
  },
  '&:hover': {
    backgroundColor: '#ffda1b',
    color: '#232536',
    fontWeight: 'bold',
    transform: 'translateY(-1px)',
  },
}))

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    fontSize: 'clamp(0.75rem, 1.1vw, 0.875rem)',
    color: '#232536',
    '& fieldset': {
      borderColor: '#e2e8f0',
    },
    '&:hover fieldset': {
      borderColor: '#232536',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#232536',
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: 'clamp(0.75rem, 1.1vw, 0.875rem)',
    color: '#232536',
    '&.Mui-focused': {
      color: '#232536',
    },
  },
  '& .MuiFormHelperText-root': {
    fontSize: 'clamp(0.6rem, 1vw, 0.75rem)',
  },
}))

const ResponsiveGradientBox = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #232536 0%, #374151 100%)',
  borderRadius: '12px',
  padding: theme.spacing(3),
  color: 'white',
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2.5),
    borderRadius: '8px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}))

const ResponsiveAvatar = styled(Avatar)(({ theme }) => ({
  width: 80,
  height: 80,
  bgcolor: '#ffda1b',
  color: '#232536',
  fontSize: '2rem',
  fontWeight: 600,
  [theme.breakpoints.down('md')]: {
    width: 70,
    height: 70,
    fontSize: '1.75rem',
  },
  [theme.breakpoints.down('sm')]: {
    width: 60,
    height: 60,
    fontSize: '1.5rem',
  },
}))

const ResponsiveContainer = styled(Container)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
}))

const ResponsivePaper = styled(Paper)(({ theme }) => ({
  borderRadius: 12,
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    borderRadius: 8,
  },
}))

const ResponsiveBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(3),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}))

const ActionButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: theme.spacing(1),
    width: '100%',
    '& .MuiButton-root': {
      width: '100%',
      justifyContent: 'center',
    },
  },
}))

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(3),
  gap: theme.spacing(2),
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(2),
  },
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
}))

const MemoizedPagination = React.memo(Pagination)

const Profile = ({
  adminUserId = null,
  adminUserSummary = null,
  embedded = false,
  syncTabWithQuery = true,
  initialSearchActivitiesPage = null,
}) => {
  const isAdminView = Boolean(adminUserId)
  const isReadOnly = isAdminView
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = syncTabWithQuery ? searchParams.get('tab') : null
  const { user, refreshQuota, quotaStatus: authQuotaStatus } = useAuth()
  const isMobile = useMediaQuery(profileTheme.breakpoints.down('sm'))
  const coverImageInputRef = useRef(null)

  const [selectedTab, setSelectedTab] = useState(() =>
    getTabIndexFromQuery(tabParam)
  )

  const [profileForm, setProfileForm] = useState(INITIAL_PROFILE_FORM)
  const [securityInfo, setSecurityInfo] = useState({
    lastLogin: null,
    accountCreated: null,
    accountLocked: false,
  })
  const [isEditing, setIsEditing] = useState(false)
  const [profileLoading, setProfileLoading] = useState(true)
  const [profileSubmitting, setProfileSubmitting] = useState(false)
  const [uploadingCoverImage, setUploadingCoverImage] = useState(false)
  const [coverLocalPreviewUrl, setCoverLocalPreviewUrl] = useState('')
  const [profileSuccess, setProfileSuccess] = useState('')
  const [profileError, setProfileError] = useState('')
  const [validationErrors, setValidationErrors] = useState({})
  const [phoneCountryCode, setPhoneCountryCode] = useState('91')
  const [mobileLocalNumber, setMobileLocalNumber] = useState('')

  const stateOptions = useMemo(() => {
    if (!profileForm.country) {
      return []
    }

    return getStateOptions(profileForm.country, profileForm.state)
  }, [profileForm.country, profileForm.state])

  const [annotations, setAnnotations] = useState([])
  const [annotationLoading, setAnnotationLoading] = useState(false)
  const [annotationItems, setAnnotationItems] = useState(0)
  const [annotationPages, setAnnotationPages] = useState(0)
  const [annotationPage, setAnnotationPage] = useState(1)
  const [annotationItemsPerPage, setAnnotationItemsPerPage] = useState(10)
  const [annotationSearchQuery, setAnnotationSearchQuery] = useState('')
  const [annotationFilters, setAnnotationFilters] = useState(
    INITIAL_ANNOTATION_FILTERS
  )

  const [bookmarks, setBookmarks] = useState([])
  const [bookmarkLoading, setBookmarkLoading] = useState(false)
  const [bookmarkItems, setBookmarkItems] = useState(0)
  const [bookmarkPages, setBookmarkPages] = useState(0)
  const [bookmarkPage, setBookmarkPage] = useState(1)
  const [bookmarkItemsPerPage, setBookmarkItemsPerPage] = useState(10)
  const [bookmarkExporting, setBookmarkExporting] = useState(false)
  const [contentShareDialogOpen, setContentShareDialogOpen] = useState(false)
  const [contentShareSourceEmail, setContentShareSourceEmail] = useState('')
  const [contentShareOtpDigits, setContentShareOtpDigits] = useState(() =>
    createEmptyOtpDigits()
  )
  const [contentShareOtpSent, setContentShareOtpSent] = useState(false)
  const [contentShareSendingOtp, setContentShareSendingOtp] = useState(false)
  const [contentShareVerifyingOtp, setContentShareVerifyingOtp] =
    useState(false)
  const [contentShareError, setContentShareError] = useState('')
  const [contentShareMessage, setContentShareMessage] = useState('')
  const [contentShareMaskedEmail, setContentShareMaskedEmail] = useState('')
  const [contentShareResendCooldownSeconds, setContentShareResendCooldownSeconds] =
    useState(0)
  const [contentShareOtpExpiresInSeconds, setContentShareOtpExpiresInSeconds] =
    useState(0)
  const [contentShareResendCooldownDeadlineMs, setContentShareResendCooldownDeadlineMs] =
    useState(0)
  const [contentShareOtpExpiresDeadlineMs, setContentShareOtpExpiresDeadlineMs] =
    useState(0)
  const [contentShareLastOtpEmail, setContentShareLastOtpEmail] = useState('')
  const contentShareOtpInputRefs = useRef([])

  const [userBlogs, setUserBlogs] = useState([])
  const [userBlogsLoading, setUserBlogsLoading] = useState(false)
  const [userBlogsPage, setUserBlogsPage] = useState(1)
  const [userBlogsItems, setUserBlogsItems] = useState(0)
  const [userBlogsPages, setUserBlogsPages] = useState(0)
  const [blogActionLoadingId, setBlogActionLoadingId] = useState('')
  const [searchActivities, setSearchActivities] = useState(() =>
    !isAdminView && Array.isArray(initialSearchActivitiesPage?.content)
      ? initialSearchActivitiesPage.content
      : []
  )
  const [searchActivityLoading, setSearchActivityLoading] = useState(false)
  const [searchActivityPage, setSearchActivityPage] = useState(() =>
    !isAdminView && Number.isFinite(initialSearchActivitiesPage?.number)
      ? initialSearchActivitiesPage.number + 1
      : 1
  )
  const [searchActivityItemsPerPage, setSearchActivityItemsPerPage] = useState(() =>
    !isAdminView && Number.isFinite(initialSearchActivitiesPage?.size)
      ? initialSearchActivitiesPage.size
      : 10
  )
  const [searchActivityItems, setSearchActivityItems] = useState(() =>
    !isAdminView && Number.isFinite(initialSearchActivitiesPage?.totalElements)
      ? initialSearchActivitiesPage.totalElements
      : 0
  )
  const [searchActivityPages, setSearchActivityPages] = useState(() =>
    !isAdminView && Number.isFinite(initialSearchActivitiesPage?.totalPages)
      ? initialSearchActivitiesPage.totalPages
      : 0
  )
  const [searchActivityFilters, setSearchActivityFilters] = useState(
    INITIAL_SEARCH_ACTIVITY_FILTERS
  )
  const initialSearchActivityConsumedRef = useRef(false)

  const [subscriptions, setSubscriptions] = useState([])
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false)
  const [payments, setPayments] = useState([])
  const [paymentsLoading, setPaymentsLoading] = useState(false)
  const [quotaStatus, setQuotaStatus] = useState(EMPTY_QUOTA_STATUS)
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [selectedSubscriptionForCancel, setSelectedSubscriptionForCancel] =
    useState(null)
  const [cancelSubmitting, setCancelSubmitting] = useState(false)
  const [autoRenewUpdatingIds, setAutoRenewUpdatingIds] = useState(() => new Set())
  const [deleteDraftDialogOpen, setDeleteDraftDialogOpen] = useState(false)
  const [selectedDraftBlogForDelete, setSelectedDraftBlogForDelete] =
    useState(null)

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  })

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity })
  }, [])

  const closeSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }))
  }, [])

  const resetContentShareVerificationState = useCallback(() => {
    setContentShareSourceEmail('')
    setContentShareOtpDigits(createEmptyOtpDigits())
    setContentShareOtpSent(false)
    setContentShareSendingOtp(false)
    setContentShareVerifyingOtp(false)
    setContentShareError('')
    setContentShareMessage('')
    setContentShareMaskedEmail('')
    setContentShareResendCooldownSeconds(0)
    setContentShareOtpExpiresInSeconds(0)
    setContentShareResendCooldownDeadlineMs(0)
    setContentShareOtpExpiresDeadlineMs(0)
    setContentShareLastOtpEmail('')
  }, [])

  useEffect(() => {
    if (contentShareResendCooldownDeadlineMs <= 0) {
      setContentShareResendCooldownSeconds(0)
      return undefined
    }

    const updateRemaining = () => {
      const remainingSeconds = toRemainingSeconds(
        contentShareResendCooldownDeadlineMs
      )
      setContentShareResendCooldownSeconds(remainingSeconds)
      if (remainingSeconds <= 0) {
        setContentShareResendCooldownDeadlineMs(0)
      }
    }
    updateRemaining()

    const timerId = window.setInterval(updateRemaining, 250)
    return () => {
      window.clearInterval(timerId)
    }
  }, [contentShareResendCooldownDeadlineMs])

  useEffect(() => {
    if (!contentShareOtpSent || contentShareOtpExpiresDeadlineMs <= 0) {
      if (!contentShareOtpSent) {
        setContentShareOtpExpiresInSeconds(0)
      }
      return undefined
    }

    const updateRemaining = () => {
      const remainingSeconds = toRemainingSeconds(
        contentShareOtpExpiresDeadlineMs
      )
      setContentShareOtpExpiresInSeconds(remainingSeconds)
      if (remainingSeconds <= 0) {
        setContentShareOtpExpiresDeadlineMs(0)
      }
    }
    updateRemaining()

    const timerId = window.setInterval(updateRemaining, 250)
    return () => {
      window.clearInterval(timerId)
    }
  }, [contentShareOtpExpiresDeadlineMs, contentShareOtpSent])

  useEffect(
    () => () => {
      if (coverLocalPreviewUrl && coverLocalPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(coverLocalPreviewUrl)
      }
    },
    [coverLocalPreviewUrl]
  )

  const notifyReviewOutcomeChanges = useCallback((blogs) => {
    if (isAdminView) {
      return
    }

    let previousState = {}
    try {
      previousState = JSON.parse(
        localStorage.getItem(BLOG_REVIEW_NOTICE_STORAGE_KEY) || '{}'
      )
    } catch (error) {
      previousState = {}
    }

    const nextState = {}
    const reworkUpdates = []
    const publishUpdates = []

    blogs.forEach((blog) => {
      if (!blog?.id) {
        return
      }

      const status = blog.reviewStatus || 'DRAFT'
      const signature = buildReviewNoticeSignature(blog)

      if (status === 'REWORK_REQUIRED' || status === 'PUBLISHED') {
        nextState[blog.id] = signature
        const previousSignature = previousState[blog.id]
        if (previousSignature !== signature) {
          if (status === 'REWORK_REQUIRED') {
            reworkUpdates.push(blog)
          } else {
            publishUpdates.push(blog)
          }
        }
      }
    })

    localStorage.setItem(
      BLOG_REVIEW_NOTICE_STORAGE_KEY,
      JSON.stringify(nextState)
    )

    if (reworkUpdates.length > 0) {
      if (reworkUpdates.length === 1) {
        showSnackbar(
          `Admin sent "${reworkUpdates[0]?.title || 'Untitled'}" for rework. Please review the notes.`,
          'warning'
        )
      } else {
        showSnackbar(
          `Admin sent ${reworkUpdates.length} blogs for rework. Please review the notes.`,
          'warning'
        )
      }
      return
    }

    if (publishUpdates.length > 0) {
      if (publishUpdates.length === 1) {
        showSnackbar(
          `"${publishUpdates[0]?.title || 'Untitled'}" is now approved and published.`,
          'success'
        )
      } else {
        showSnackbar(
          `${publishUpdates.length} blogs are now approved and published.`,
          'success'
        )
      }
    }
  }, [isAdminView, showSnackbar])

  const viewedUserRoles = useMemo(() => {
    const rawRoles = isAdminView ? adminUserSummary?.roles : user?.roles
    if (!rawRoles || typeof rawRoles !== 'string') {
      return []
    }
    return rawRoles
      .split(',')
      .map((role) => role.trim().toUpperCase())
      .filter(Boolean)
  }, [adminUserSummary?.roles, isAdminView, user?.roles])

  const viewedUserIsAdmin = viewedUserRoles.includes('ADMIN') ||
    viewedUserRoles.includes('ROLE_ADMIN')

  const publicProfileUsername = useMemo(() => {
    const rawUsername = profileForm?.username
    if (!rawUsername || typeof rawUsername !== 'string') {
      return ''
    }

    const trimmed = rawUsername.trim()
    if (!trimmed) {
      return ''
    }

    return trimmed.startsWith('@') ? trimmed.slice(1) : trimmed
  }, [profileForm?.username])

  const loadProfile = useCallback(
    async (forceRefresh = false) => {
      try {
        setProfileLoading(true)
        const options = forceRefresh
          ? { bypassCache: true, skipCache: true }
          : {}
        const profileData = isAdminView
          ? await apiService.getAdminUserProfile(adminUserId, options)
          : await apiService.get('/api/v1/profile', {}, options)
        const normalizedCountry = profileData?.country
          ? resolveCountryCode(profileData.country)
          : ''
        const fallbackPhoneCode = normalizedCountry
          ? getCountryPhoneCode(normalizedCountry)
          : '91'
        const normalizedStoredMobile = splitStoredMobileNumber(
          profileData?.mobileNumber || '',
          fallbackPhoneCode
        )

        setProfileForm({
          name: profileData?.name || '',
          username: profileData?.username || '',
          email: profileData?.email || '',
          company: profileData?.company || '',
          location: profileData?.location || '',
          bio: profileData?.bio || '',
          coverPhotoUrl: profileData?.coverPhotoUrl || '',
          street: profileData?.street || '',
          city: profileData?.city || '',
          state: normalizedCountry ? profileData?.state || '' : '',
          postalCode: profileData?.postalCode || '',
          country: normalizedCountry,
          profilePictureUrl: profileData?.profilePictureUrl || '',
        })
        setPhoneCountryCode(
          normalizedStoredMobile.phoneCode || fallbackPhoneCode || '91'
        )
        setMobileLocalNumber(normalizedStoredMobile.localNumber || '')

        setSecurityInfo({
          lastLogin: convertArrayToDate(profileData?.lastLogin),
          accountCreated: convertArrayToDate(profileData?.createdAt),
          accountLocked: Boolean(profileData?.accountLocked),
        })

        setProfileError('')
      } catch (error) {
        console.error('Failed to load profile data:', error)
        setProfileError('Failed to load profile data')
      } finally {
        setProfileLoading(false)
      }
    },
    [adminUserId, isAdminView]
  )

  const loadBookmarkCount = useCallback(async (forceRefresh = false) => {
    try {
      const options = forceRefresh ? { bypassCache: true, skipCache: true } : {}
      const bookmarkParams = {
        page: 0,
        size: 1,
        sortBy: 'createdAt',
        sortDir: 'desc',
      }
      const response = isAdminView
        ? await apiService.getAdminUserBookmarks(
            adminUserId,
            bookmarkParams,
            options
          )
        : await apiService.get('/api/v1/bookmarks', bookmarkParams, options)

      setBookmarkItems(response?.totalElements || 0)
      setBookmarkPages(response?.totalPages || 0)
    } catch (error) {
      console.error('Failed to load bookmark count:', error)
    }
  }, [adminUserId, isAdminView])

  const loadAnnotationCount = useCallback(async (forceRefresh = false) => {
    try {
      const annotationParams = {
        query: null,
        category: null,
        priority: null,
        isImportant: null,
        tags: null,
        page: 0,
        size: 1,
        sortBy: 'createdAt',
        sortDir: 'desc',
        ...(forceRefresh ? { _t: Date.now() } : {}),
      }
      const response = isAdminView
        ? await apiService.searchAdminUserAnnotations(adminUserId, annotationParams)
        : await apiService.searchUserAnnotations(annotationParams)

      setAnnotationItems(response?.totalElements || 0)
      setAnnotationPages(response?.totalPages || 0)
    } catch (error) {
      console.error('Failed to load annotation count:', error)
      setAnnotationItems(0)
      setAnnotationPages(0)
    }
  }, [adminUserId, isAdminView])

  const loadBookmarks = useCallback(
    async ({ page, size }) => {
      try {
        setBookmarkLoading(true)
        const bookmarkParams = {
          page: Math.max(page - 1, 0),
          size,
          sortBy: 'createdAt',
          sortDir: 'desc',
        }
        const response = isAdminView
          ? await apiService.getAdminUserBookmarks(
              adminUserId,
              bookmarkParams,
              { bypassCache: true, skipCache: true }
            )
          : await apiService.get(
              '/api/v1/bookmarks',
              bookmarkParams,
              { bypassCache: true, skipCache: true }
            )

        const rows = response?.content || []
        setBookmarks(rows)
        setBookmarkItems(response?.totalElements || 0)
        setBookmarkPages(response?.totalPages || 0)
        setBookmarkPage((response?.number ?? 0) + 1)
      } catch (error) {
        console.error('Failed to load bookmarks:', error)
        showSnackbar('Failed to load bookmarks', 'error')
      } finally {
        setBookmarkLoading(false)
      }
    },
    [adminUserId, isAdminView, showSnackbar]
  )

  const loadUserBlogs = useCallback(
    async (page = userBlogsPage) => {
      if (isAdminView) {
        setUserBlogs([])
        setUserBlogsItems(0)
        setUserBlogsPages(0)
        return
      }
      try {
        setUserBlogsLoading(true)
        const response = await apiService.getMyBlogs(page, USER_BLOGS_PAGE_SIZE)
        const rows = Array.isArray(response?.content) ? response.content : []
        setUserBlogs(rows)
        setUserBlogsItems(response?.totalElements ?? rows.length)
        setUserBlogsPages(response?.totalPages ?? 0)
        setUserBlogsPage((response?.number ?? Math.max(page - 1, 0)) + 1)
        notifyReviewOutcomeChanges(rows)
      } catch (error) {
        console.error('Failed to load user blogs:', error)
        showSnackbar('Failed to load blogs', 'error')
        setUserBlogs([])
        setUserBlogsItems(0)
        setUserBlogsPages(0)
      } finally {
        setUserBlogsLoading(false)
      }
    },
    [isAdminView, notifyReviewOutcomeChanges, showSnackbar, userBlogsPage]
  )

  const loadUserBlogsCount = useCallback(async () => {
    if (isAdminView) {
      setUserBlogsItems(0)
      setUserBlogsPages(0)
      return
    }

    try {
      const response = await apiService.getMyBlogs(1, 1, {
        bypassCache: true,
        skipCache: true,
      })
      setUserBlogsItems(response?.totalElements || 0)
      setUserBlogsPages(response?.totalPages || 0)
    } catch (error) {
      console.error('Failed to load user blog count:', error)
      setUserBlogsItems(0)
      setUserBlogsPages(0)
    }
  }, [isAdminView])

  const loadSearchActivities = useCallback(
    async ({ page, size, filters, forceRefresh = false }) => {
      if (isAdminView) {
        setSearchActivities([])
        setSearchActivityItems(0)
        setSearchActivityPages(0)
        return
      }

      try {
        setSearchActivityLoading(true)
        const params = {
          page: Math.max(page - 1, 0),
          size,
        }
        if (filters?.type && filters.type !== 'ALL') {
          params.type = filters.type
        }
        if (filters?.country) {
          params.country = filters.country
        }
        if (filters?.law) {
          params.law = filters.law
        }
        if (filters?.dateFrom) {
          params.dateFrom = filters.dateFrom
        }
        if (filters?.dateTo) {
          params.dateTo = filters.dateTo
        }
        const options = forceRefresh
          ? {
              bypassCache: true,
              skipCache: true,
              cancelPrevious: true,
              abortKey: 'profile-search-activities',
            }
          : {
              cancelPrevious: true,
              abortKey: 'profile-search-activities',
            }
        const response = await apiService.getUserSearchActivities(params, options)
        const rows = Array.isArray(response?.content) ? response.content : []
        setSearchActivities(rows)
        setSearchActivityItems(response?.totalElements ?? rows.length)
        setSearchActivityPages(response?.totalPages ?? 0)
        setSearchActivityPage((response?.number ?? Math.max(page - 1, 0)) + 1)
      } catch (error) {
        if (apiService.isRequestCanceled?.(error) || error?.isCanceled) {
          return
        }
        console.error('Failed to load search activity:', error)
        showSnackbar('Failed to load search activity', 'error')
        setSearchActivities([])
        setSearchActivityItems(0)
        setSearchActivityPages(0)
      } finally {
        setSearchActivityLoading(false)
      }
    },
    [isAdminView, showSnackbar]
  )

  const loadAnnotations = useCallback(
    async ({ page, size, query, filters, forceRefresh = false }) => {
      try {
        setAnnotationLoading(true)
        const annotationParams = {
          query: query?.trim() || null,
          category: filters.category !== 'all' ? filters.category : null,
          priority: filters.priority !== 'all' ? filters.priority : null,
          isImportant:
            filters.isImportant !== 'all'
              ? filters.isImportant === 'true'
              : null,
          tags: filters.tags?.length ? filters.tags : null,
          page: Math.max(page - 1, 0),
          size,
          sortBy: 'createdAt',
          sortDir: 'desc',
          ...(forceRefresh ? { _t: Date.now() } : {}),
        }
        const response = isAdminView
          ? await apiService.searchAdminUserAnnotations(adminUserId, annotationParams)
          : await apiService.searchUserAnnotations(annotationParams)

        const rows = Array.isArray(response)
          ? response
          : Array.isArray(response?.content)
          ? response.content
          : []

        setAnnotations(rows)
        setAnnotationItems(response?.totalElements ?? rows.length)
        setAnnotationPages(
          response?.totalPages ?? Math.ceil(rows.length / Math.max(size, 1)) ?? 0
        )
        setAnnotationPage((response?.number ?? Math.max(page - 1, 0)) + 1)
      } catch (error) {
        console.error('Failed to load annotations:', error)
        showSnackbar('Failed to load annotations', 'error')
        setAnnotations([])
        setAnnotationItems(0)
        setAnnotationPages(0)
      } finally {
        setAnnotationLoading(false)
      }
    },
    [adminUserId, isAdminView, showSnackbar]
  )

  const loadSubscriptions = useCallback(
    async () => {
      try {
        setSubscriptionsLoading(true)
        const data = isAdminView
          ? await apiService.getAdminUserSubscriptionHistory(adminUserId, {
              bypassCache: true,
              skipCache: true,
            })
          : await apiService.get(
              '/api/v1/subscriptions/my-subscriptions/history',
              {},
              { bypassCache: true, skipCache: true }
            )

        const normalized = Array.isArray(data)
          ? data
          : Array.isArray(data?.content)
          ? data.content
          : []

        setSubscriptions(normalized)
      } catch (error) {
        console.error('Failed to load subscription history:', error)
        showSnackbar('Failed to load subscription history', 'error')
        setSubscriptions([])
      } finally {
        setSubscriptionsLoading(false)
      }
    },
    [adminUserId, isAdminView, showSnackbar]
  )

  const loadQuotaStatus = useCallback(async () => {
    try {
      if (!isAdminView && typeof refreshQuota === 'function') {
        const refreshed = await refreshQuota()
        if (refreshed) {
          setQuotaStatus(refreshed)
          return
        }
      }
      const quota = isAdminView
        ? await apiService.getAdminUserQuotaStatus(adminUserId, {
            bypassCache: true,
            skipCache: true,
          })
        : await apiService.get(
            '/api/v1/quota/status',
            {},
            { bypassCache: true, skipCache: true }
          )
      setQuotaStatus(quota || EMPTY_QUOTA_STATUS)
    } catch (error) {
      console.error('Failed to load quota status:', error)
      setQuotaStatus(EMPTY_QUOTA_STATUS)
    }
  }, [adminUserId, isAdminView, refreshQuota])

  const loadPayments = useCallback(async () => {
    try {
      setPaymentsLoading(true)
      const data = isAdminView
        ? await apiService.getAdminUserPaymentHistory(adminUserId, {
            bypassCache: true,
            skipCache: true,
          })
        : await apiService.getUserPaymentHistory({
            bypassCache: true,
            skipCache: true,
          })

      const normalized = Array.isArray(data)
        ? data
        : Array.isArray(data?.content)
        ? data.content
        : []

      const sortedPayments = [...normalized].sort(
        (left, right) =>
          getPaymentSortTimestamp(right) - getPaymentSortTimestamp(left)
      )
      setPayments(sortedPayments)
    } catch (error) {
      console.error('Failed to load payment history:', error)
      showSnackbar('Failed to load payment history', 'error')
      setPayments([])
    } finally {
      setPaymentsLoading(false)
    }
  }, [adminUserId, isAdminView, showSnackbar])

  useEffect(() => {
    if (!syncTabWithQuery) {
      return
    }
    const nextTab = getTabIndexFromQuery(tabParam)
    setSelectedTab(nextTab)
  }, [syncTabWithQuery, tabParam])

  useEffect(() => {
    if (!isAdminView && authQuotaStatus) {
      setQuotaStatus(authQuotaStatus)
    }
  }, [authQuotaStatus, isAdminView])

  useEffect(() => {
    if (!profileSuccess) {
      return undefined
    }

    const timeoutId = setTimeout(() => {
      setProfileSuccess('')
    }, 5000)

    return () => clearTimeout(timeoutId)
  }, [profileSuccess])

  useEffect(() => {
    if (isAdminView) {
      if (!adminUserId) {
        return
      }
      loadProfile(true)
      loadBookmarkCount(true)
      loadAnnotationCount(true)
      loadUserBlogsCount()
      loadQuotaStatus()
      return
    }

    if (location.pathname === '/profile') {
      loadProfile(true)
      loadBookmarkCount(true)
      loadAnnotationCount(true)
      loadUserBlogsCount()
      loadQuotaStatus()
    }
  }, [
    adminUserId,
    isAdminView,
    location.pathname,
    loadProfile,
    loadBookmarkCount,
    loadAnnotationCount,
    loadUserBlogsCount,
    loadQuotaStatus,
  ])

  useEffect(() => {
    if (selectedTab !== TAB_KEYS.annotations) {
      return
    }

    loadAnnotations({
      page: annotationPage,
      size: annotationItemsPerPage,
      query: annotationSearchQuery,
      filters: annotationFilters,
      forceRefresh: true,
    })
  }, [
    selectedTab,
    annotationPage,
    annotationItemsPerPage,
    annotationSearchQuery,
    annotationFilters,
    loadAnnotations,
  ])

  useEffect(() => {
    if (selectedTab !== TAB_KEYS.bookmarks) {
      return
    }

    loadBookmarks({
      page: bookmarkPage,
      size: bookmarkItemsPerPage,
    })
  }, [
    selectedTab,
    bookmarkPage,
    bookmarkItemsPerPage,
    loadBookmarks,
  ])

  useEffect(() => {
    if (selectedTab !== TAB_KEYS.blogs) {
      return
    }
    loadUserBlogs(userBlogsPage)
  }, [selectedTab, loadUserBlogs, userBlogsPage])

  useEffect(() => {
    if (selectedTab !== TAB_KEYS.searchActivity || isAdminView) {
      return
    }

    const hasActiveSearchActivityFilters = Boolean(
      (searchActivityFilters.type &&
        searchActivityFilters.type !== INITIAL_SEARCH_ACTIVITY_FILTERS.type) ||
        searchActivityFilters.country ||
        searchActivityFilters.law ||
        searchActivityFilters.dateFrom ||
        searchActivityFilters.dateTo
    )

    const hasInitialPage =
      Array.isArray(initialSearchActivitiesPage?.content) &&
      Number.isFinite(initialSearchActivitiesPage?.number) &&
      Number.isFinite(initialSearchActivitiesPage?.size)
    const isInitialRequest =
      hasInitialPage &&
      !hasActiveSearchActivityFilters &&
      searchActivityPage === initialSearchActivitiesPage.number + 1 &&
      searchActivityItemsPerPage === initialSearchActivitiesPage.size

    if (isInitialRequest && !initialSearchActivityConsumedRef.current) {
      initialSearchActivityConsumedRef.current = true
      return
    }

    loadSearchActivities({
      page: searchActivityPage,
      size: searchActivityItemsPerPage,
      filters: searchActivityFilters,
      forceRefresh: true,
    })
  }, [
    searchActivityFilters,
    initialSearchActivitiesPage,
    isAdminView,
    loadSearchActivities,
    searchActivityItemsPerPage,
    searchActivityPage,
    selectedTab,
  ])

  useEffect(() => {
    if (selectedTab !== TAB_KEYS.subscriptions) {
      return
    }

    loadSubscriptions()
    loadQuotaStatus()
  }, [selectedTab, loadSubscriptions, loadQuotaStatus])

  useEffect(() => {
    if (selectedTab !== TAB_KEYS.payments) {
      return
    }

    loadPayments()
  }, [selectedTab, loadPayments])

  const handleTabChange = useCallback(
    (event, newValue) => {
      setSelectedTab(newValue)
      if (syncTabWithQuery) {
        const nextParams = new URLSearchParams(searchParams)
        nextParams.set('tab', TAB_QUERY[newValue] || 'annotations')
        setSearchParams(nextParams, { replace: true })
      }
    },
    [searchParams, setSearchParams, syncTabWithQuery]
  )

  const handleDeleteBookmark = useCallback(
    (bookmarkId) => {
      if (isReadOnly) {
        return
      }
      setBookmarks((prev) =>
        prev.filter((bookmark) => bookmark.id !== bookmarkId)
      )
      setBookmarkItems((prev) => Math.max(0, prev - 1))
      showSnackbar('Bookmark deleted successfully', 'success')
    },
    [isReadOnly, showSnackbar]
  )

  const handleUpdateBookmark = useCallback((updatedBookmark) => {
    setBookmarks((prev) =>
      prev.map((bookmark) =>
        bookmark.id === updatedBookmark.id ? updatedBookmark : bookmark
      )
    )
  }, [])

  const handleDeleteAnnotation = useCallback(
    async (annotationId) => {
      if (isReadOnly) {
        return
      }
      try {
        setAnnotationLoading(true)
        await apiService.deleteAnnotation(annotationId)

        setAnnotations((prev) =>
          prev.filter((annotation) => annotation.id !== annotationId)
        )
        setAnnotationItems((prev) => Math.max(0, prev - 1))
        showSnackbar('Annotation deleted successfully', 'success')
      } catch (error) {
        console.error('Failed to delete annotation:', error)
        showSnackbar('Failed to delete annotation', 'error')
        loadAnnotations({
          page: annotationPage,
          size: annotationItemsPerPage,
          query: annotationSearchQuery,
          filters: annotationFilters,
          forceRefresh: true,
        })
      } finally {
        setAnnotationLoading(false)
      }
    },
    [
      isReadOnly,
      annotationPage,
      annotationItemsPerPage,
      annotationSearchQuery,
      annotationFilters,
      loadAnnotations,
      showSnackbar,
    ]
  )

  const handleUpdateAnnotation = useCallback((updatedAnnotation) => {
    setAnnotations((prev) =>
      prev.map((annotation) =>
        annotation.id === updatedAnnotation.id ? updatedAnnotation : annotation
      )
    )
  }, [])

  const handleAnnotationSearchInputChange = useCallback((query) => {
    setAnnotationSearchQuery(query)
  }, [])

  const handleAnnotationSearch = useCallback((query) => {
    setAnnotationSearchQuery(query)
    setAnnotationPage(1)
  }, [])

  const handleAnnotationFiltersChange = useCallback((filters) => {
    setAnnotationFilters(filters)
    setAnnotationPage(1)
  }, [])

  const handleAnnotationItemsPerPageChange = useCallback((newItemsPerPage) => {
    setAnnotationItemsPerPage(newItemsPerPage)
    setAnnotationPage(1)
  }, [])

  const handleBookmarkItemsPerPageChange = useCallback((newItemsPerPage) => {
    setBookmarkItemsPerPage(newItemsPerPage)
    setBookmarkPage(1)
  }, [])

  const handleSearchActivityItemsPerPageChange = useCallback(
    (newItemsPerPage) => {
      setSearchActivityItemsPerPage(newItemsPerPage)
      setSearchActivityPage(1)
    },
    []
  )

  const handleSearchActivityFiltersChange = useCallback((nextFilters) => {
    setSearchActivityFilters((prev) => ({
      ...prev,
      ...nextFilters,
    }))
    setSearchActivityPage(1)
  }, [])

  const handleOpenSearchActivityResult = useCallback(
    (searchPath) => {
      if (!searchPath) {
        return
      }
      navigate(searchPath)
    },
    [navigate]
  )

  const handleOpenContentShareDialog = useCallback(() => {
    resetContentShareVerificationState()
    setContentShareDialogOpen(true)
  }, [resetContentShareVerificationState])

  const handleCloseContentShareDialog = useCallback(() => {
    if (contentShareSendingOtp || contentShareVerifyingOtp) {
      return
    }
    setContentShareDialogOpen(false)
    resetContentShareVerificationState()
  }, [
    contentShareSendingOtp,
    contentShareVerifyingOtp,
    resetContentShareVerificationState,
  ])

  const handleSendContentShareVerificationCode = useCallback(
    async ({ ignoreLocalCooldown = false } = {}) => {
      const email = contentShareSourceEmail.trim()
      const normalizedEmail = normalizeEmailValue(email)
      if (!email) {
        setContentShareError('Email is required')
        return
      }

      const isSameEmailAsLastOtp =
        !!contentShareLastOtpEmail &&
        normalizeEmailValue(contentShareLastOtpEmail) === normalizedEmail
      const isSendBlockedByCooldown =
        contentShareResendCooldownSeconds > 0 && isSameEmailAsLastOtp

      if (!ignoreLocalCooldown && isSendBlockedByCooldown) {
        setContentShareError(
          `Please wait ${contentShareResendCooldownSeconds}s before requesting another code.`
        )
        return
      }

      setContentShareSendingOtp(true)
      setContentShareError('')
      setContentShareMessage('')

      try {
        const response = await apiService.sendProfileContentShareVerificationCode(
          email
        )

        if (response?.success) {
          const resolvedMaskedEmail =
            response?.maskedEmail || maskEmailForDisplay(normalizedEmail)
          const deliveryQueued = response?.deliveryStatus === 'queued'

          setContentShareSourceEmail(email)
          setContentShareOtpSent(true)
          setContentShareOtpDigits(createEmptyOtpDigits())
          setContentShareLastOtpEmail(normalizedEmail)
          setContentShareMaskedEmail(resolvedMaskedEmail)
          const resolvedResendCooldownSeconds =
            Number(response?.resendCooldownSeconds) || 30
          const resolvedOtpExpiresInSeconds =
            Number(response?.expiresInSeconds) || 600
          const nowMs = getMonotonicNowMs()
          setContentShareResendCooldownSeconds(resolvedResendCooldownSeconds)
          setContentShareOtpExpiresInSeconds(resolvedOtpExpiresInSeconds)
          setContentShareResendCooldownDeadlineMs(
            nowMs + resolvedResendCooldownSeconds * 1000
          )
          setContentShareOtpExpiresDeadlineMs(
            nowMs + resolvedOtpExpiresInSeconds * 1000
          )
          setContentShareMessage(
            `${
              deliveryQueued
                ? 'Verification code is on the way to'
                : 'Verification code sent to'
            } ${resolvedMaskedEmail}`
          )

          window.requestAnimationFrame(() => {
            contentShareOtpInputRefs.current[0]?.focus()
          })
        } else {
          setContentShareError(
            response?.error || 'Failed to send verification code.'
          )
        }
      } catch (err) {
        const retryAfterSeconds = Number(
          err.response?.data?.retryAfterSeconds || 0
        )
        if (retryAfterSeconds > 0) {
          setContentShareResendCooldownSeconds(retryAfterSeconds)
          setContentShareResendCooldownDeadlineMs(
            getMonotonicNowMs() + retryAfterSeconds * 1000
          )
        }
        setContentShareError(
          extractBackendError(err) ||
            err.userMessage ||
            'Failed to send verification code.'
        )
      } finally {
        setContentShareSendingOtp(false)
      }
    },
    [
      contentShareLastOtpEmail,
      contentShareResendCooldownSeconds,
      contentShareSourceEmail,
    ]
  )

  const handleVerifyContentShareCode = useCallback(async () => {
    const email = contentShareSourceEmail.trim()
    const otp = contentShareOtpDigits.join('')

    if (!email) {
      setContentShareError('Email is required')
      return
    }
    if (!otp || !/^\d{6}$/.test(otp)) {
      setContentShareError('Please enter the 6-digit verification code.')
      return
    }
    if (contentShareOtpExpiresInSeconds <= 0) {
      setContentShareError('Verification code expired. Please request a new code.')
      return
    }

    setContentShareVerifyingOtp(true)
    setContentShareError('')
    setContentShareMessage('')

    try {
      const response = await apiService.verifyProfileContentShareCodeAndImport({
        email,
        otp,
        includeBookmarks: true,
        includeAnnotations: true,
      })

      if (!response?.success) {
        setContentShareError(response?.error || 'Verification failed.')
        return
      }

      const importedBookmarks = Number(response?.importedBookmarks || 0)
      const importedAnnotations = Number(response?.importedAnnotations || 0)
      const totalImported = Number(
        response?.totalImported || importedBookmarks + importedAnnotations
      )

      await Promise.all([loadBookmarkCount(true), loadAnnotationCount(true)])
      if (selectedTab === TAB_KEYS.bookmarks) {
        await loadBookmarks({
          page: bookmarkPage,
          size: bookmarkItemsPerPage,
        })
      }
      if (selectedTab === TAB_KEYS.annotations) {
        await loadAnnotations({
          page: annotationPage,
          size: annotationItemsPerPage,
          query: annotationSearchQuery,
          filters: annotationFilters,
          forceRefresh: true,
        })
      }

      showSnackbar(
        totalImported > 0
          ? `Imported ${totalImported} item${
              totalImported !== 1 ? 's' : ''
            } (${importedBookmarks} bookmarks, ${importedAnnotations} annotations).`
          : 'No new bookmarks or annotations were imported.',
        totalImported > 0 ? 'success' : 'info'
      )

      setContentShareDialogOpen(false)
      resetContentShareVerificationState()
    } catch (err) {
      setContentShareError(
        extractBackendError(err) ||
          err.userMessage ||
          'Failed to verify code and import content.'
      )
    } finally {
      setContentShareVerifyingOtp(false)
    }
  }, [
    annotationFilters,
    annotationItemsPerPage,
    annotationPage,
    annotationSearchQuery,
    bookmarkItemsPerPage,
    bookmarkPage,
    contentShareOtpDigits,
    contentShareOtpExpiresInSeconds,
    contentShareSourceEmail,
    loadAnnotationCount,
    loadAnnotations,
    loadBookmarkCount,
    loadBookmarks,
    resetContentShareVerificationState,
    selectedTab,
    showSnackbar,
  ])

  const handleContentShareOtpDigitChange = useCallback((index, event) => {
    const enteredDigits = event.target.value.replace(/\D/g, '')
    if (!enteredDigits) {
      setContentShareOtpDigits((prevDigits) => {
        const nextDigits = [...prevDigits]
        nextDigits[index] = ''
        return nextDigits
      })
      return
    }

    setContentShareOtpDigits((prevDigits) => {
      const nextDigits = [...prevDigits]
      enteredDigits.split('').forEach((digit, offset) => {
        const nextIndex = index + offset
        if (nextIndex < nextDigits.length) {
          nextDigits[nextIndex] = digit
        }
      })
      return nextDigits
    })

    const focusIndex = index + enteredDigits.length
    if (focusIndex < contentShareOtpDigits.length) {
      window.requestAnimationFrame(() => {
        contentShareOtpInputRefs.current[focusIndex]?.focus()
      })
    }
  }, [contentShareOtpDigits.length])

  const handleContentShareOtpDigitKeyDown = useCallback(
    async (index, event) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        if (!contentShareVerifyingOtp && !contentShareSendingOtp) {
          await handleVerifyContentShareCode()
        }
        return
      }

      if (event.key === 'Backspace') {
        event.preventDefault()
        const hasCurrentDigit = !!contentShareOtpDigits[index]
        setContentShareOtpDigits((prevDigits) => {
          const nextDigits = [...prevDigits]
          if (hasCurrentDigit) {
            nextDigits[index] = ''
            return nextDigits
          }
          if (index > 0) {
            nextDigits[index - 1] = ''
          }
          return nextDigits
        })

        if (!hasCurrentDigit && index > 0) {
          window.requestAnimationFrame(() => {
            contentShareOtpInputRefs.current[index - 1]?.focus()
          })
        }
        return
      }

      if (event.key === 'ArrowLeft' && index > 0) {
        event.preventDefault()
        contentShareOtpInputRefs.current[index - 1]?.focus()
        return
      }

      if (event.key === 'ArrowRight' && index < contentShareOtpDigits.length - 1) {
        event.preventDefault()
        contentShareOtpInputRefs.current[index + 1]?.focus()
      }
    },
    [
      contentShareOtpDigits,
      contentShareSendingOtp,
      contentShareVerifyingOtp,
      handleVerifyContentShareCode,
    ]
  )

  const handleContentShareOtpPaste = useCallback((event) => {
    const pastedDigits = event.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, contentShareOtpDigits.length)

    if (!pastedDigits) {
      return
    }

    event.preventDefault()
    const nextDigits = createEmptyOtpDigits()
    pastedDigits.split('').forEach((digit, index) => {
      nextDigits[index] = digit
    })
    setContentShareOtpDigits(nextDigits)

    const lastDigitIndex = Math.min(pastedDigits.length, nextDigits.length) - 1
    window.requestAnimationFrame(() => {
      contentShareOtpInputRefs.current[Math.max(lastDigitIndex, 0)]?.focus()
    })
  }, [contentShareOtpDigits.length])

  const handleRestartContentShareWithNewEmail = useCallback(() => {
    setContentShareOtpSent(false)
    setContentShareOtpDigits(createEmptyOtpDigits())
    setContentShareError('')
    setContentShareMessage('')
    setContentShareMaskedEmail('')
    setContentShareResendCooldownSeconds(0)
    setContentShareOtpExpiresInSeconds(0)
    setContentShareResendCooldownDeadlineMs(0)
    setContentShareOtpExpiresDeadlineMs(0)
    setContentShareLastOtpEmail('')
  }, [])

  const handleOpenBlogWriter = useCallback(
    (blogId = null) => {
      if (blogId) {
        navigate(`/blogs/write?blogId=${encodeURIComponent(blogId)}`)
        return
      }
      navigate('/blogs/write')
    },
    [navigate]
  )

  const handleWithdrawBlog = useCallback(
    async (blogId) => {
      try {
        setBlogActionLoadingId(blogId)
        await apiService.withdrawBlogFromReview(blogId)
        showSnackbar('Blog withdrawn from review', 'success')
        await loadUserBlogs(userBlogsPage)
      } catch (error) {
        console.error('Failed to withdraw blog:', error)
        showSnackbar(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            'Failed to withdraw blog',
          'error'
        )
      } finally {
        setBlogActionLoadingId('')
      }
    },
    [loadUserBlogs, showSnackbar, userBlogsPage]
  )

  const handleDeleteDraftBlog = useCallback((blog) => {
    if (!blog?.id) {
      return
    }
    setSelectedDraftBlogForDelete(blog)
    setDeleteDraftDialogOpen(true)
  }, [])

  const handleCloseDeleteDraftDialog = useCallback(() => {
    if (blogActionLoadingId === selectedDraftBlogForDelete?.id) {
      return
    }
    setDeleteDraftDialogOpen(false)
    setSelectedDraftBlogForDelete(null)
  }, [blogActionLoadingId, selectedDraftBlogForDelete])

  const handleConfirmDeleteDraftBlog = useCallback(async () => {
    const blogId = selectedDraftBlogForDelete?.id
    if (!blogId) {
      return
    }

    try {
      setBlogActionLoadingId(blogId)
      await apiService.deleteMyDraftBlog(blogId)

      const scopedDraftKey = `blog_writer_local_draft::${blogId}`
      localStorage.removeItem(scopedDraftKey)

      const activeDraftNamespace = localStorage.getItem(
        'blog_writer_asset_namespace',
      )
      if (activeDraftNamespace === blogId) {
        localStorage.removeItem('blog_writer_asset_namespace')
      }

      setDeleteDraftDialogOpen(false)
      setSelectedDraftBlogForDelete(null)
      showSnackbar('Draft blog deleted successfully', 'success')
      await loadUserBlogs(userBlogsPage)
    } catch (error) {
      console.error('Failed to delete draft blog:', error)
      showSnackbar(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Failed to delete draft blog',
        'error',
      )
    } finally {
      setBlogActionLoadingId('')
    }
  }, [loadUserBlogs, selectedDraftBlogForDelete, showSnackbar, userBlogsPage])

  const fetchAnnotationsForExport = useCallback(
    async (params) => {
      if (isAdminView) {
        return apiService.searchAdminUserAnnotations(adminUserId, params)
      }
      return apiService.searchUserAnnotations(params)
    },
    [adminUserId, isAdminView]
  )

  const handleExportAllBookmarks = useCallback(async () => {
    if (bookmarkExporting) {
      return
    }

    try {
      setBookmarkExporting(true)
      const pageSize = 500
      let page = 0
      let hasNext = true
      const allBookmarks = []

      while (hasNext) {
        const params = {
          page,
          size: pageSize,
          sortBy: 'createdAt',
          sortDir: 'desc',
        }

        const response = isAdminView
          ? await apiService.getAdminUserBookmarks(adminUserId, params, {
              bypassCache: true,
              skipCache: true,
            })
          : await apiService.get('/api/v1/bookmarks', params, {
              bypassCache: true,
              skipCache: true,
            })

        const rows = Array.isArray(response?.content) ? response.content : []
        allBookmarks.push(...rows)

        const nextFromFlag =
          typeof response?.hasNext === 'boolean' ? response.hasNext : null
        const nextFromPages =
          response?.totalPages !== undefined
            ? page + 1 < response.totalPages
            : false
        hasNext = nextFromFlag !== null ? nextFromFlag : nextFromPages
        page += 1

        // Safety guard against unexpected API behavior
        if (page > 1000) {
          hasNext = false
        }
      }

      if (allBookmarks.length === 0) {
        showSnackbar('No bookmarks available for export', 'info')
        return
      }

      const emailPart = (profileForm?.email || adminUserSummary?.email || 'user')
        .split('@')[0]
      exportBookmarksToCSV(allBookmarks, `bookmarks_${emailPart}`)
      showSnackbar(
        `Exported ${allBookmarks.length} bookmark${
          allBookmarks.length !== 1 ? 's' : ''
        }`,
        'success'
      )
    } catch (error) {
      console.error('Failed to export bookmarks:', error)
      showSnackbar('Failed to export bookmarks', 'error')
    } finally {
      setBookmarkExporting(false)
    }
  }, [
    adminUserId,
    adminUserSummary?.email,
    bookmarkExporting,
    isAdminView,
    profileForm?.email,
    showSnackbar,
  ])

  const handleCancelSubscription = useCallback((subscription) => {
    if (isReadOnly) {
      return
    }
    if (!subscription?.id) {
      return
    }

    setSelectedSubscriptionForCancel(subscription)
    setCancelDialogOpen(true)
  }, [isReadOnly])

  const handleCloseCancelDialog = useCallback(() => {
    if (cancelSubmitting) {
      return
    }

    setCancelDialogOpen(false)
    setSelectedSubscriptionForCancel(null)
  }, [cancelSubmitting])

  const handleConfirmCancelSubscription = useCallback(async () => {
    if (isReadOnly) {
      return
    }
    if (!selectedSubscriptionForCancel?.id) {
      return
    }

    try {
      setCancelSubmitting(true)
      await apiService.cancelSubscription(
        selectedSubscriptionForCancel.id,
        'User requested cancellation via profile'
      )

      setCancelDialogOpen(false)
      setSelectedSubscriptionForCancel(null)
      showSnackbar('Subscription cancelled successfully', 'success')
      await Promise.all([loadSubscriptions(), loadQuotaStatus()])
    } catch (error) {
      console.error('Failed to cancel subscription:', error)
      showSnackbar(
        error.response?.data?.message || 'Failed to cancel subscription',
        'error'
      )
    } finally {
      setCancelSubmitting(false)
    }
  }, [
    isReadOnly,
    selectedSubscriptionForCancel,
    showSnackbar,
    loadSubscriptions,
    loadQuotaStatus,
  ])

  const handleToggleAutoRenew = useCallback(
    async (subscriptionId, currentAutoRenew) => {
      if (isReadOnly) {
        return
      }
      const nextAutoRenew = !currentAutoRenew
      setAutoRenewUpdatingIds((prev) => {
        const next = new Set(prev)
        next.add(subscriptionId)
        return next
      })
      setSubscriptions((prev) =>
        prev.map((subscription) =>
          subscription.id === subscriptionId
            ? { ...subscription, autoRenew: nextAutoRenew }
            : subscription
        )
      )
      try {
        await apiService.updateSubscriptionAutoRenew(
          subscriptionId,
          nextAutoRenew
        )
        showSnackbar(
          `Auto-renewal ${nextAutoRenew ? 'enabled' : 'disabled'}`,
          'success'
        )
      } catch (error) {
        setSubscriptions((prev) =>
          prev.map((subscription) =>
            subscription.id === subscriptionId
              ? { ...subscription, autoRenew: currentAutoRenew }
              : subscription
          )
        )
        console.error('Failed to update auto-renewal:', error)
        showSnackbar(
          error.response?.data?.message || 'Failed to update auto-renewal',
          'error'
        )
      } finally {
        setAutoRenewUpdatingIds((prev) => {
          const next = new Set(prev)
          next.delete(subscriptionId)
          return next
        })
      }
    },
    [isReadOnly, showSnackbar]
  )

  const handleFormChange = useCallback(
    (event) => {
      const { name, value } = event.target

      if (name === 'country') {
        const normalizedCountry = value ? resolveCountryCode(value) : ''

        setProfileForm((prev) => {
          if (!normalizedCountry) {
            return {
              ...prev,
              country: '',
              state: '',
            }
          }

          const nextStateOptions = getStateOptions(normalizedCountry)
          const hasCurrentState = nextStateOptions.some(
            (state) => state.value === prev.state
          )

          return {
            ...prev,
            country: normalizedCountry,
            state: hasCurrentState ? prev.state : nextStateOptions[0]?.value || '',
          }
        })

        if (normalizedCountry) {
          setPhoneCountryCode(getCountryPhoneCode(normalizedCountry))
        }
      } else {
        setProfileForm((prev) => ({
          ...prev,
          [name]: value,
        }))
      }

      if (validationErrors[name]) {
        setValidationErrors((prev) => {
          const nextErrors = { ...prev }
          delete nextErrors[name]
          return nextErrors
        })
      }

      if (name === 'country' && validationErrors.state) {
        setValidationErrors((prev) => {
          const nextErrors = { ...prev }
          delete nextErrors.state
          return nextErrors
        })
      }
    },
    [validationErrors]
  )

  const handlePhoneCountryCodeChange = useCallback(
    (event) => {
      setPhoneCountryCode(normalizeLocalMobileNumber(event.target.value))

      if (validationErrors.mobileNumber) {
        setValidationErrors((prev) => {
          const nextErrors = { ...prev }
          delete nextErrors.mobileNumber
          return nextErrors
        })
      }
    },
    [validationErrors.mobileNumber]
  )

  const handleMobileNumberChange = useCallback(
    (event) => {
      setMobileLocalNumber(normalizeLocalMobileNumber(event.target.value))

      if (validationErrors.mobileNumber) {
        setValidationErrors((prev) => {
          const nextErrors = { ...prev }
          delete nextErrors.mobileNumber
          return nextErrors
        })
      }
    },
    [validationErrors.mobileNumber]
  )

  const handleCoverImageUpload = useCallback(
    async (event) => {
      const file = event.target.files?.[0]
      if (!file) {
        return
      }

      if (!COVER_ALLOWED_TYPES.includes(file.type)) {
        showSnackbar('Only JPG, PNG, and WEBP files are allowed.', 'error')
        event.target.value = ''
        return
      }

      if (file.size > COVER_MAX_IMAGE_SIZE_BYTES) {
        showSnackbar('Cover image must be 10MB or smaller.', 'error')
        event.target.value = ''
        return
      }

      try {
        setUploadingCoverImage(true)
        const response = await apiService.uploadProfileCoverImage(file)
        const nextUrl = response?.url || ''
        if (!nextUrl) {
          throw new Error('Upload did not return image URL')
        }

        setProfileForm((prev) => ({
          ...prev,
          coverPhotoUrl: nextUrl,
        }))
        setValidationErrors((prev) => {
          if (!prev.coverPhotoUrl) {
            return prev
          }
          const next = { ...prev }
          delete next.coverPhotoUrl
          return next
        })

        setCoverLocalPreviewUrl((previous) => {
          if (previous && previous.startsWith('blob:')) {
            URL.revokeObjectURL(previous)
          }
          return ''
        })

        showSnackbar('Cover image uploaded successfully. Save profile to persist.', 'success')
      } catch (error) {
        console.error('Cover image upload failed:', error)
        const errorMessage =
          error?.response?.data?.message ||
          error?.userMessage ||
          error?.message ||
          'Failed to upload cover image'
        showSnackbar(errorMessage, 'error')
      } finally {
        setUploadingCoverImage(false)
        event.target.value = ''
      }
    },
    [showSnackbar]
  )

  const handleSaveProfile = useCallback(async () => {
    try {
      setProfileSubmitting(true)
      setProfileError('')
      setProfileSuccess('')
      setValidationErrors({})

      const mobileValidation = validateAndFormatMobileNumber({
        phoneCode: phoneCountryCode,
        localNumber: mobileLocalNumber,
        fallbackCountryCode: profileForm.country,
      })

      if (mobileLocalNumber && !mobileValidation.isValid) {
        setValidationErrors({
          mobileNumber: mobileValidation.error,
        })
        return
      }

      const hasAddressData = Boolean(
        profileForm.street?.trim() ||
          profileForm.city?.trim() ||
          profileForm.state?.trim() ||
          profileForm.postalCode?.trim() ||
          profileForm.country?.trim()
      )

      const updateData = {
        name: profileForm.name,
        username: profileForm.username,
        mobileNumber: mobileValidation.isValid ? mobileValidation.storedNumber : '',
        company: profileForm.company,
        location: profileForm.location,
        bio: profileForm.bio,
        coverPhotoUrl: profileForm.coverPhotoUrl,
        ...(hasAddressData && {
          street: profileForm.street,
          city: profileForm.city,
          state: profileForm.state,
          postalCode: profileForm.postalCode,
          country: profileForm.country ? resolveCountryCode(profileForm.country) : '',
        }),
      }

      const updatedProfile = await apiService.put('/api/v1/profile', updateData)
      const normalizedCountry = updatedProfile?.country
        ? resolveCountryCode(updatedProfile.country)
        : ''
      const fallbackPhoneCode = normalizedCountry
        ? getCountryPhoneCode(normalizedCountry)
        : '91'
      const normalizedStoredMobile = splitStoredMobileNumber(
        updatedProfile?.mobileNumber || '',
        fallbackPhoneCode
      )

      setProfileForm((prev) => ({
        ...prev,
        ...updatedProfile,
        state: normalizedCountry ? updatedProfile?.state || '' : '',
        country: normalizedCountry,
      }))
      setPhoneCountryCode(
        normalizedStoredMobile.phoneCode || fallbackPhoneCode || '91'
      )
      setMobileLocalNumber(normalizedStoredMobile.localNumber || '')
      setCoverLocalPreviewUrl((previous) => {
        if (previous && previous.startsWith('blob:')) {
          URL.revokeObjectURL(previous)
        }
        return ''
      })

      setProfileSuccess('Profile updated successfully!')
      setIsEditing(false)
    } catch (error) {
      console.error('Failed to update profile:', error)
      if (error.response?.status === 400 && typeof error.response?.data === 'object') {
        setValidationErrors(error.response.data)
      } else {
        setProfileError(error.userMessage || 'Failed to update profile')
      }
    } finally {
      setProfileSubmitting(false)
    }
  }, [mobileLocalNumber, phoneCountryCode, profileForm])

  const handleCancelProfileEditing = useCallback(() => {
    setIsEditing(false)
    setValidationErrors({})
    setProfileError('')
    setProfileSuccess('')
    setCoverLocalPreviewUrl((previous) => {
      if (previous && previous.startsWith('blob:')) {
        URL.revokeObjectURL(previous)
      }
      return ''
    })
    loadProfile(true)
  }, [loadProfile])

  const showBookmarkPagination = useMemo(
    () => bookmarkItems > bookmarkItemsPerPage && bookmarkPages > 1,
    [bookmarkItems, bookmarkItemsPerPage, bookmarkPages]
  )

  const showAnnotationPagination = useMemo(
    () => annotationItems > annotationItemsPerPage && annotationPages > 1,
    [annotationItems, annotationItemsPerPage, annotationPages]
  )

  const activeSubscriptionCountFromHistory = useMemo(
    () =>
      subscriptions.filter((sub) => {
        const startDate = convertArrayToDate(sub?.startDate)
        const hasStarted = !startDate || startDate.getTime() <= Date.now()
        const isActive = sub?.status === 'Active'
        const isCancelledButStillActive =
          sub?.status === 'Cancelled' &&
          sub?.isActive &&
          !sub?.isExpired &&
          hasStarted
        const hasLiveAccessFallback =
          !sub?.status && sub?.isActive && !sub?.isExpired && hasStarted

        return isActive || isCancelledButStillActive || hasLiveAccessFallback
      }).length,
    [subscriptions]
  )

  const subscriptionTabCount = useMemo(() => {
    if (subscriptionsLoading) {
      return quotaStatus?.activeSubscriptions?.length ?? 0
    }
    if (subscriptions.length > 0) {
      return activeSubscriptionCountFromHistory
    }
    return quotaStatus?.activeSubscriptions?.length ?? 0
  }, [
    activeSubscriptionCountFromHistory,
    quotaStatus,
    subscriptions.length,
    subscriptionsLoading,
  ])

  const annotationExportOptions = useMemo(() => {
    const filename = isAdminView
      ? `user_annotations_${(profileForm?.email || adminUserSummary?.email || 'user')
          .split('@')[0]}`
      : 'gtl_annotations'

    return {
      fetchAnnotations: fetchAnnotationsForExport,
      filename,
    }
  }, [
    adminUserSummary?.email,
    fetchAnnotationsForExport,
    isAdminView,
    profileForm?.email,
  ])

  const contentShareOtpValue = contentShareOtpDigits.join('')
  const contentShareIsOtpComplete = /^\d{6}$/.test(contentShareOtpValue)
  const contentShareIsSameEmailAsLastOtp =
    !!contentShareLastOtpEmail &&
    normalizeEmailValue(contentShareLastOtpEmail) ===
      normalizeEmailValue(contentShareSourceEmail)
  const contentShareIsSendBlockedByCooldown =
    contentShareResendCooldownSeconds > 0 && contentShareIsSameEmailAsLastOtp
  const contentShareActiveMaskedEmail =
    contentShareMaskedEmail || maskEmailForDisplay(contentShareSourceEmail)
  const contentShareIsOtpExpired =
    contentShareOtpSent && contentShareOtpExpiresInSeconds <= 0

  if (profileLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '400px',
          px: 2,
        }}
      >
        <CircularProgress size={isMobile ? 30 : 40} />
        <Typography sx={{ ml: 2 }} variant={isMobile ? 'body2' : 'body1'}>
          Loading profile...
        </Typography>
      </Box>
    )
  }

  return (
    <ThemeProvider theme={profileTheme}>
      <Box
        sx={{
          minHeight: embedded ? 'auto' : '100vh',
          backgroundColor: embedded ? 'transparent' : '#f9fafb',
          py: embedded ? 0 : { xs: 2, sm: 3, md: 4 },
        }}
      >
        <ResponsiveContainer
          maxWidth={embedded ? false : 'lg'}
          disableGutters={embedded}
        >
          <ResponsiveGradientBox>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 2, sm: 3 },
                flexDirection: { xs: 'column', sm: 'row' },
                textAlign: { xs: 'center', sm: 'left' },
              }}
            >
              <ResponsiveAvatar
                src={getProxiedUrl(profileForm.profilePictureUrl)}
                alt={profileForm.name}
              >
                {profileForm.name?.charAt(0)?.toUpperCase() || 'U'}
              </ResponsiveAvatar>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant={isMobile ? 'h5' : 'h4'}
                  sx={{ fontWeight: 600, mb: 1, wordBreak: 'break-word' }}
                >
                  {profileForm.name}
                </Typography>
                <Typography
                  variant={isMobile ? 'body2' : 'body1'}
                  sx={{ color: '#e2e8f0', mb: 1, wordBreak: 'break-all' }}
                >
                  {profileForm.email}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    flexWrap: 'wrap',
                    justifyContent: { xs: 'center', sm: 'flex-start' },
                    alignItems: 'center',
                  }}
                >
                  <Chip
                    label={viewedUserIsAdmin ? 'Administrator' : 'Member'}
                    sx={{
                      bgcolor: '#ffda1b',
                      color: '#232536',
                      fontWeight: 700,
                      fontSize: { xs: '0.8rem', sm: '0.95rem' },
                      letterSpacing: '0.05rem',
                      height: isMobile ? 31 : 36,
                      borderRadius: '4px',
                    }}
                    size={isMobile ? 'small' : 'medium'}
                  />
                  {!isAdminView && publicProfileUsername && (
                    <Button
                      variant='outlined'
                      size={isMobile ? 'small' : 'medium'}
                      disabled={isEditing}
                      onClick={() => navigate(`/@${publicProfileUsername}`)}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        color: '#ffda1b',
                        borderColor: 'rgba(255, 218, 27, 0.8)',
                        '&:hover': {
                          borderColor: '#ffda1b',
                          backgroundColor: 'rgba(255, 218, 27, 0.12)',
                        },
                        '&.Mui-disabled': {
                          color: 'rgba(255, 218, 27, 0.55)',
                          borderColor: 'rgba(255, 218, 27, 0.35)',
                        },
                      }}
                    >
                      View Public Profile
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          </ResponsiveGradientBox>

          {selectedTab === TAB_KEYS.personal && profileSuccess && (
            <Alert severity='success' sx={{ mb: 3, borderRadius: 2 }}>
              {profileSuccess}
            </Alert>
          )}

          {selectedTab === TAB_KEYS.personal && profileError && (
            <Alert severity='error' sx={{ mb: 3, borderRadius: 2 }}>
              {profileError}
            </Alert>
          )}

          <ResponsivePaper>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              variant='scrollable'
              scrollButtons='auto'
              allowScrollButtonsMobile={true}
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTabs-scroller': {
                  overflowX: 'auto !important',
                },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: { xs: '0.85rem', sm: '0.95rem' },
                  minWidth: { xs: 'auto', sm: 120 },
                  px: { xs: 1.5, sm: 2.5 },
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                },
                '& .MuiTab-root.Mui-selected': {
                  color: '#232536',
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: themeColors.secondary,
                },
                '& .MuiTabs-scrollButtons': {
                  color: '#232536',
                  '&.Mui-disabled': {
                    opacity: 0.3,
                  },
                },
              }}
            >
              <Tab
                icon={
                  <NotesRoundedIcon fontSize={isMobile ? 'small' : 'medium'} />
                }
                label={`Annotations (${annotationItems})`}
                iconPosition={isMobile ? 'top' : 'start'}
              />
              <Tab
                icon={<Bookmark fontSize={isMobile ? 'small' : 'medium'} />}
                label={`Bookmarks (${bookmarkItems})`}
                iconPosition={isMobile ? 'top' : 'start'}
              />
              <Tab
                icon={<Article fontSize={isMobile ? 'small' : 'medium'} />}
                label={`My Blogs (${userBlogsItems})`}
                iconPosition={isMobile ? 'top' : 'start'}
              />
              <Tab
                icon={<TrendingUp fontSize={isMobile ? 'small' : 'medium'} />}
                label='Search Activity'
                iconPosition={isMobile ? 'top' : 'start'}
              />
              <Tab
                icon={<Person fontSize={isMobile ? 'small' : 'medium'} />}
                label='Personal Info'
                iconPosition={isMobile ? 'top' : 'start'}
              />
              <Tab
                icon={<CardMembership />}
                label={`Subscriptions (${subscriptionTabCount})`}
                iconPosition={isMobile ? 'top' : 'start'}
              />
              <Tab
                icon={<PaymentsIcon />}
                label='Payments'
                iconPosition={isMobile ? 'top' : 'start'}
              />
            </Tabs>

            <ResponsiveBox>
              {selectedTab === TAB_KEYS.annotations && (
                <Box>
                  {!isReadOnly && (
                    <Stack
                      direction='row'
                      justifyContent='flex-end'
                      sx={{ mb: 2 }}
                    >
                      <Button
                        variant='outlined'
                        startIcon={<Email />}
                        onClick={handleOpenContentShareDialog}
                        sx={{
                          borderColor: '#232536',
                          color: '#232536',
                          '&:hover': {
                            borderColor: '#232536',
                            bgcolor: '#ffcf51',
                            color: '#232536',
                          },
                        }}
                      >
                        Import from Email
                      </Button>
                    </Stack>
                  )}
                  <AnnotationsSection
                    annotations={annotations}
                    loading={annotationLoading}
                    searchQuery={annotationSearchQuery}
                    filters={annotationFilters}
                    onSearchChange={handleAnnotationSearchInputChange}
                    onFiltersChange={handleAnnotationFiltersChange}
                    onSearch={handleAnnotationSearch}
                    onDelete={handleDeleteAnnotation}
                    onUpdate={handleUpdateAnnotation}
                    onOpen={() => {}}
                    totalItems={annotationItems}
                    currentPage={annotationPage}
                    itemsPerPage={annotationItemsPerPage}
                    totalPages={annotationPages}
                    onPageChange={setAnnotationPage}
                    onItemsPerPageChange={handleAnnotationItemsPerPageChange}
                    showPagination={showAnnotationPagination}
                    isMobile={isMobile}
                    themeColors={themeColors}
                    readOnly={isReadOnly}
                    showSnackbar={showSnackbar}
                    exportOptions={annotationExportOptions}
                  />
                </Box>
              )}

              {selectedTab === TAB_KEYS.bookmarks && (
                <Box>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent='end'
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    spacing={2}
                    sx={{ mb: 3 }}
                  >
                    {!isReadOnly && (
                      <Button
                        variant='outlined'
                        startIcon={<Email />}
                        onClick={handleOpenContentShareDialog}
                        sx={{
                          borderColor: '#232536',
                          color: '#232536',
                          '&:hover': {
                            borderColor: '#232536',
                            bgcolor: '#ffcf51',
                            color: '#232536',
                          },
                        }}
                      >
                        Import from Email
                      </Button>
                    )}
                    {bookmarkItems > 0 && (
                      <Button
                        variant='outlined'
                        startIcon={
                          bookmarkExporting ? (
                            <CircularProgress size={16} color='inherit' />
                          ) : (
                            <Download />
                          )
                        }
                        onClick={handleExportAllBookmarks}
                        disabled={bookmarkExporting}
                        sx={{
                          borderColor: '#232536',
                          color: '#232536',
                          '&:hover': {
                            borderColor: '#232536',
                            bgcolor: '#ffcf51',
                            color: '#232536',
                          },
                        }}
                      >
                        {bookmarkExporting
                          ? 'Exporting...'
                          : 'Export All Bookmarks'}
                      </Button>
                    )}
                    <ItemsPerPageSelector
                      itemsPerPage={bookmarkItemsPerPage}
                      setItemsPerPage={handleBookmarkItemsPerPageChange}
                      loading={bookmarkLoading}
                    />
                  </Stack>

                  {bookmarkLoading && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        py: 4,
                      }}
                    >
                      <CircularProgress size={isMobile ? 30 : 40} />
                      <Typography sx={{ ml: 2 }}>
                        Loading bookmarks...
                      </Typography>
                    </Box>
                  )}

                  {!bookmarkLoading &&
                    (bookmarks.length > 0 ? (
                      <>
                        {bookmarks.some((bookmark) => bookmark.isStarred) && (
                          <>
                            <Stack
                              direction='row'
                              alignItems='center'
                              spacing={1}
                              sx={{ mb: 2 }}
                            >
                              <Star sx={{ color: '#ffda1b' }} />
                              <Typography
                                variant={isMobile ? 'subtitle1' : 'h6'}
                                sx={{
                                  fontWeight: 600,
                                  mb: 2,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  color: '#232536',
                                }}
                              >
                                Important Bookmarks (
                                {
                                  bookmarks.filter(
                                    (bookmark) => bookmark.isStarred,
                                  ).length
                                }
                                )
                              </Typography>
                            </Stack>

                            {bookmarks
                              .filter((bookmark) => bookmark.isStarred)
                              .map((bookmark) => (
                                <EnhancedBookmarkCard
                                  key={bookmark.id}
                                  bookmark={bookmark}
                                  onDelete={handleDeleteBookmark}
                                  onUpdate={handleUpdateBookmark}
                                  readOnly={isReadOnly}
                                  showSnackbar={showSnackbar}
                                />
                              ))}

                            {bookmarks.some(
                              (bookmark) => !bookmark.isStarred,
                            ) && (
                              <>
                                <Divider sx={{ my: 3 }} />
                                <Stack
                                  direction='row'
                                  alignItems='center'
                                  spacing={1}
                                  sx={{ mb: 2 }}
                                >
                                  <Category sx={{ color: '#666' }} />
                                  <Typography
                                    variant={isMobile ? 'subtitle1' : 'h6'}
                                    sx={{
                                      fontWeight: 600,
                                      mb: 2,
                                      color: '#232536',
                                    }}
                                  >
                                    Other Bookmarks (
                                    {
                                      bookmarks.filter(
                                        (bookmark) => !bookmark.isStarred,
                                      ).length
                                    }
                                    )
                                  </Typography>
                                </Stack>
                              </>
                            )}
                          </>
                        )}

                        {bookmarks
                          .filter((bookmark) => !bookmark.isStarred)
                          .map((bookmark) => (
                            <EnhancedBookmarkCard
                              key={bookmark.id}
                              bookmark={bookmark}
                              onDelete={handleDeleteBookmark}
                              onUpdate={handleUpdateBookmark}
                              readOnly={isReadOnly}
                              showSnackbar={showSnackbar}
                            />
                          ))}

                        {showBookmarkPagination && (
                          <MemoizedPagination
                            itemsPerPage={bookmarkItemsPerPage}
                            totalItems={bookmarkItems}
                            currentPage={bookmarkPage}
                            paginate={setBookmarkPage}
                          />
                        )}
                      </>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 8 } }}>
                        <Typography
                          variant={isMobile ? 'subtitle1' : 'h6'}
                          color='text.secondary'
                          sx={{ mb: 2 }}
                        >
                          No bookmarks saved yet
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                          Start bookmarking articles to see them here!
                        </Typography>
                      </Box>
                    ))}
                </Box>
              )}

              {selectedTab === TAB_KEYS.blogs && (
                <Box>
                  <HeaderContainer>
                    <Typography
                      variant={isMobile ? 'h6' : 'h5'}
                      sx={{ fontWeight: 600 }}
                    >
                      My Blog Submissions
                    </Typography>
                    {!isReadOnly && (
                      <StyledButton onClick={() => handleOpenBlogWriter()}>
                        Write New Blog
                      </StyledButton>
                    )}
                  </HeaderContainer>

                  {isAdminView ? (
                    <Alert severity='info' sx={{ borderRadius: 2 }}>
                      Blog management is available in your own profile view.
                    </Alert>
                  ) : userBlogsLoading ? (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        py: 4,
                      }}
                    >
                      <CircularProgress size={isMobile ? 30 : 40} />
                      <Typography sx={{ ml: 2 }}>Loading blogs...</Typography>
                    </Box>
                  ) : userBlogs.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 8 } }}>
                      <Typography
                        variant={isMobile ? 'subtitle1' : 'h6'}
                        color='text.secondary'
                        sx={{ mb: 1 }}
                      >
                        No blogs yet
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        Start writing your first blog and submit it for admin
                        review.
                      </Typography>
                    </Box>
                  ) : (
                    <Stack spacing={1.5}>
                      {userBlogs.map((blog) => {
                        const status = blog?.reviewStatus || 'DRAFT'
                        const isDraft = status === 'DRAFT'
                        const isEditable =
                          status === 'DRAFT' || status === 'REWORK_REQUIRED'
                        const isWithdrawable = status === 'PENDING_REVIEW'
                        const isPublished = status === 'PUBLISHED' && blog?.slug

                        return (
                          <Paper
                            key={blog.id}
                            variant='outlined'
                            sx={{
                              p: 2,
                              borderColor: '#e2e8f0',
                            }}
                          >
                            <Stack
                              direction={{ xs: 'column', md: 'row' }}
                              spacing={2}
                              justifyContent='space-between'
                              alignItems={{ xs: 'flex-start', md: 'center' }}
                            >
                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  variant='subtitle1'
                                  sx={{
                                    fontWeight: 600,
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  {blog.title || 'Untitled'}
                                </Typography>
                                <Stack
                                  direction='row'
                                  spacing={1}
                                  sx={{ mt: 0.75 }}
                                  useFlexGap
                                  flexWrap='wrap'
                                >
                                  <Chip size='small' label={status} />
                                  <Chip
                                    size='small'
                                    label={blog?.category || 'General'}
                                    sx={{
                                      backgroundColor: '#ffcf51',
                                      color: '#232536',
                                    }}
                                  />
                                  <Chip
                                    size='small'
                                    label={`Updated ${formatBlogTimestamp(blog?.updatedDate)}`}
                                  />
                                </Stack>
                                {blog?.reviewNotes && (
                                  <Typography
                                    variant='body2'
                                    color='text.secondary'
                                    sx={{ mt: 1, whiteSpace: 'pre-wrap' }}
                                  >
                                    Rework Notes: {blog.reviewNotes}
                                  </Typography>
                                )}
                              </Box>

                              <Stack
                                direction='row'
                                spacing={1}
                                flexWrap='wrap'
                                useFlexGap
                              >
                                {isEditable && (
                                  <Button
                                    variant='outlined'
                                    startIcon={<Edit />}
                                    onClick={() =>
                                      handleOpenBlogWriter(blog.id)
                                    }
                                    sx={{
                                      borderColor: '#232536',
                                      color: '#232536',
                                      textTransform: 'none',
                                      '&:hover': {
                                        borderColor: '#3a3e5a',
                                        color: '#3a3e5a',
                                        backgroundColor: 'rgba(35, 37, 54, 0.04)',
                                      },
                                    }}
                                  >
                                    Edit
                                  </Button>
                                )}
                                {isDraft && (
                                  <Button
                                    variant='outlined'
                                    color='error'
                                    startIcon={
                                      blogActionLoadingId === blog.id ? (
                                        <CircularProgress size={14} />
                                      ) : (
                                        <DeleteOutline />
                                      )
                                    }
                                    disabled={blogActionLoadingId === blog.id}
                                    onClick={() => handleDeleteDraftBlog(blog)}
                                    sx={{ textTransform: 'none' }}
                                  >
                                    Delete Draft
                                  </Button>
                                )}
                                {isWithdrawable && (
                                  <Button
                                    variant='outlined'
                                    color='warning'
                                    startIcon={
                                      blogActionLoadingId === blog.id ? (
                                        <CircularProgress size={14} />
                                      ) : (
                                        <Autorenew />
                                      )
                                    }
                                    disabled={blogActionLoadingId === blog.id}
                                    onClick={() => handleWithdrawBlog(blog.id)}
                                    sx={{ textTransform: 'none' }}
                                  >
                                    Withdraw
                                  </Button>
                                )}
                                {isPublished && (
                                  <Button
                                    variant='text'
                                    onClick={() =>
                                      navigate(`/blogs/${blog.slug}`)
                                    }
                                    sx={{
                                      color: '#232536',
                                      textTransform: 'none',
                                    }}
                                  >
                                    View
                                  </Button>
                                )}
                              </Stack>
                            </Stack>
                          </Paper>
                        )
                      })}

                      {userBlogsPages > 1 && (
                        <MemoizedPagination
                          itemsPerPage={USER_BLOGS_PAGE_SIZE}
                          totalItems={userBlogsItems}
                          currentPage={userBlogsPage}
                          paginate={setUserBlogsPage}
                        />
                      )}
                    </Stack>
                  )}
                </Box>
              )}

              {selectedTab === TAB_KEYS.searchActivity && (
                <Box>
                  {isAdminView ? (
                    <Alert severity='info' sx={{ borderRadius: 2 }}>
                      Search activity is available in your own profile view.
                    </Alert>
                  ) : (
                    <SearchActivityTab
                      activities={searchActivities}
                      loading={searchActivityLoading}
                      totalItems={searchActivityItems}
                      totalPages={searchActivityPages}
                      currentPage={searchActivityPage}
                      itemsPerPage={searchActivityItemsPerPage}
                      filters={searchActivityFilters}
                      onPageChange={setSearchActivityPage}
                      onItemsPerPageChange={handleSearchActivityItemsPerPageChange}
                      onFiltersChange={handleSearchActivityFiltersChange}
                      onOpenSearch={handleOpenSearchActivityResult}
                    />
                  )}
                </Box>
              )}

              {selectedTab === TAB_KEYS.personal && (
                <Box>
                  <Paper
                    sx={{
                      p: { xs: 2, sm: 3 },
                      mb: 3,
                      bgcolor: '#f8fafc',
                      border: '1px solid #e2e8f0',
                    }}
                  >
                    <Typography
                      variant={isMobile ? 'subtitle1' : 'h6'}
                      sx={{
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Security sx={{ color: '#ffda1b' }} />
                      Account Information
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant='body2' color='text.secondary'>
                          Account Type
                        </Typography>
                        <Typography variant='body1' sx={{ fontWeight: 500 }}>
                          {viewedUserIsAdmin
                            ? 'Administrator Account'
                            : 'Normal Account'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant='body2' color='text.secondary'>
                          Last Login
                        </Typography>
                        <Typography variant='body1' sx={{ fontWeight: 500 }}>
                          {securityInfo.lastLogin
                            ? securityInfo.lastLogin.toLocaleString()
                            : 'Never'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant='body2' color='text.secondary'>
                          Account Created
                        </Typography>
                        <Typography variant='body1' sx={{ fontWeight: 500 }}>
                          {securityInfo.accountCreated
                            ? securityInfo.accountCreated.toLocaleDateString()
                            : 'Unknown'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant='body2' color='text.secondary'>
                          Account Status
                        </Typography>
                        <Chip
                          label={
                            securityInfo.accountLocked ? 'Locked' : 'Active'
                          }
                          color={
                            securityInfo.accountLocked ? 'error' : 'success'
                          }
                          size='small'
                          sx={{ mt: 0.5 }}
                        />
                      </Grid>
                    </Grid>
                  </Paper>

                  <HeaderContainer>
                    <Typography
                      variant={isMobile ? 'h6' : 'h5'}
                      sx={{ fontWeight: 600 }}
                    >
                      Personal Information
                    </Typography>

                    {!isReadOnly && !isEditing ? (
                      <Button
                        startIcon={<Edit />}
                        onClick={() => setIsEditing(true)}
                        sx={{ color: '#232536' }}
                      >
                        Edit Profile
                      </Button>
                    ) : !isReadOnly ? (
                      <ActionButtonsContainer>
                        <StyledButton
                          startIcon={<Save />}
                          onClick={handleSaveProfile}
                          disabled={profileSubmitting}
                          variant='contained'
                        >
                          {profileSubmitting ? 'Saving...' : 'Save Changes'}
                        </StyledButton>
                        <Button
                          startIcon={<Cancel />}
                          onClick={handleCancelProfileEditing}
                          color='secondary'
                        >
                          Cancel
                        </Button>
                      </ActionButtonsContainer>
                    ) : (
                      <Chip label='Read-only view' size='small' />
                    )}
                  </HeaderContainer>

                  <Grid container spacing={{ xs: 2, sm: 3 }}>
                    <Grid item xs={12} sm={6}>
                      <StyledTextField
                        fullWidth
                        label='Full Name'
                        name='name'
                        value={profileForm.name}
                        onChange={handleFormChange}
                        disabled={isReadOnly || !isEditing}
                        error={Boolean(validationErrors.name)}
                        helperText={validationErrors.name}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position='start'>
                              <Person sx={{ color: 'text.secondary' }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiInputLabel-root.Mui-focused': {
                            color: '#232536',
                          },
                          '& .MuiOutlinedInput-root': {
                            borderRadius: { xs: 1.5, md: 2 },
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                            '&:hover fieldset': {
                              borderColor: '#232536',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#232536',
                            },
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <StyledTextField
                        fullWidth
                        label='Email'
                        name='email'
                        value={profileForm.email}
                        disabled
                        helperText='Email cannot be changed'
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position='start'>
                              <Email sx={{ color: 'text.secondary' }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: { xs: 1.5, md: 2 },
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                            '&:hover fieldset': {
                              borderColor: '#232536',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#232536',
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <StyledTextField
                        fullWidth
                        label='Username'
                        name='username'
                        value={profileForm.username}
                        onChange={handleFormChange}
                        disabled={isReadOnly || !isEditing}
                        error={Boolean(validationErrors.username)}
                        helperText={
                          validationErrors.username ||
                          'Public profile handle, letters/numbers/underscore'
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position='start'>
                              <Person sx={{ color: 'text.secondary' }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <StyledTextField
                        fullWidth
                        label='Company'
                        name='company'
                        value={profileForm.company}
                        onChange={handleFormChange}
                        disabled={isReadOnly || !isEditing}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position='start'>
                              <Business sx={{ color: 'text.secondary' }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Grid container spacing={1.5}>
                        <Grid item xs={12} sm={5}>
                          <StyledTextField
                            select
                            fullWidth
                            label='Code'
                            value={phoneCountryCode}
                            onChange={handlePhoneCountryCodeChange}
                            disabled={isReadOnly || !isEditing}
                          >
                            {PHONE_CODE_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </StyledTextField>
                        </Grid>
                        <Grid item xs={12} sm={7}>
                          <StyledTextField
                            fullWidth
                            label='Mobile Number'
                            name='mobileNumber'
                            value={mobileLocalNumber}
                            onChange={handleMobileNumberChange}
                            disabled={isReadOnly || !isEditing}
                            error={Boolean(validationErrors.mobileNumber)}
                            helperText={
                              validationErrors.mobileNumber ||
                              `Enter number only (without country code), up to ${MOBILE_NUMBER_LOCAL_MAX_DIGITS} digits`
                            }
                            inputProps={{
                              maxLength: MOBILE_NUMBER_LOCAL_MAX_DIGITS,
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position='start'>
                                  <Phone sx={{ color: 'text.secondary' }} />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <StyledTextField
                        fullWidth
                        label='Location'
                        name='location'
                        value={profileForm.location}
                        onChange={handleFormChange}
                        disabled={isReadOnly || !isEditing}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position='start'>
                              <LocationOn sx={{ color: 'text.secondary' }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <StyledTextField
                        fullWidth
                        multiline
                        rows={isMobile ? 2 : 3}
                        label='Professional Bio'
                        name='bio'
                        value={profileForm.bio}
                        onChange={handleFormChange}
                        disabled={isReadOnly || !isEditing}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          border: '1px dashed #bfc8d8',
                          borderRadius: 2,
                          p: 2,
                          backgroundColor: '#fbfcff',
                        }}
                      >
                        <Typography
                          variant='subtitle2'
                          sx={{ color: themeColors.primary, fontWeight: 700 }}
                        >
                          Cover Photo
                        </Typography>
                        <Typography
                          variant='caption'
                          sx={{
                            color: 'text.secondary',
                            display: 'block',
                            mb: 1,
                          }}
                        >
                          Upload JPG, PNG, or WEBP (max 10MB). Recommended
                          minimum 1200x675.
                        </Typography>
                        <Stack direction='row' spacing={1} alignItems='center'>
                          <Button
                            variant='outlined'
                            startIcon={
                              uploadingCoverImage ? (
                                <CircularProgress size={16} />
                              ) : (
                                <AddPhotoAlternate />
                              )
                            }
                            onClick={() => coverImageInputRef.current?.click()}
                            disabled={
                              isReadOnly || !isEditing || uploadingCoverImage
                            }
                            sx={{
                              borderColor: themeColors.primary,
                              color: themeColors.primary,
                              textTransform: 'none',
                              '&:hover': {
                                borderColor: themeColors.hover,
                                color: themeColors.hover,
                              },
                            }}
                          >
                            {profileForm.coverPhotoUrl
                              ? 'Replace Cover'
                              : 'Upload Cover'}
                          </Button>
                          {profileForm.coverPhotoUrl && (
                            <Button
                              variant='text'
                              color='error'
                              startIcon={<DeleteOutline />}
                              disabled={isReadOnly || !isEditing}
                              onClick={() => {
                                setProfileForm((prev) => ({
                                  ...prev,
                                  coverPhotoUrl: '',
                                }))
                                setCoverLocalPreviewUrl((previous) => {
                                  if (
                                    previous &&
                                    previous.startsWith('blob:')
                                  ) {
                                    URL.revokeObjectURL(previous)
                                  }
                                  return ''
                                })
                              }}
                              sx={{ textTransform: 'none' }}
                            >
                              Remove
                            </Button>
                          )}
                        </Stack>

                        <input
                          ref={coverImageInputRef}
                          type='file'
                          accept={COVER_ALLOWED_TYPES.join(',')}
                          style={{ display: 'none' }}
                          onChange={handleCoverImageUpload}
                        />

                        {profileForm.coverPhotoUrl && (
                          <Box
                            sx={{
                              mt: 2,
                              width: '100%',
                              minHeight: { xs: 170, sm: 220, md: 'auto' },
                              borderRadius: 1.5,
                              border: '1px solid #dbe2ef',
                              backgroundColor: '#f8fafc',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: { xs: 'hidden', md: 'visible' },
                            }}
                          >
                            <Box
                              component='img'
                              src={profileForm.coverPhotoUrl}
                              alt='Cover preview'
                              sx={{
                                width: '100%',
                                height: 'auto',
                                maxHeight: { xs: 280, sm: 340, md: 'none' },
                                objectFit: 'contain',
                                display: 'block',
                              }}
                            />
                          </Box>
                        )}
                        {validationErrors.coverPhotoUrl && (
                          <Typography
                            variant='caption'
                            sx={{
                              color: 'error.main',
                              display: 'block',
                              mt: 1,
                            }}
                          >
                            {validationErrors.coverPhotoUrl}
                          </Typography>
                        )}
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography
                        variant={isMobile ? 'subtitle1' : 'h6'}
                        sx={{ fontWeight: 600, mt: { xs: 2, sm: 3 }, mb: 2 }}
                      >
                        Address Information
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <StyledTextField
                        fullWidth
                        label='Street Address'
                        name='street'
                        value={profileForm.street}
                        onChange={handleFormChange}
                        disabled={isReadOnly || !isEditing}
                        error={Boolean(validationErrors.street)}
                        helperText={validationErrors.street}
                        placeholder='Enter your street address'
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <StyledTextField
                        fullWidth
                        label='City'
                        name='city'
                        value={profileForm.city}
                        onChange={handleFormChange}
                        disabled={isReadOnly || !isEditing}
                        error={Boolean(validationErrors.city)}
                        helperText={validationErrors.city}
                        placeholder='Enter your city'
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <StyledTextField
                        select
                        fullWidth
                        label='State/Province'
                        name='state'
                        value={profileForm.state}
                        onChange={handleFormChange}
                        disabled={
                          isReadOnly || !isEditing || !profileForm.country
                        }
                        error={Boolean(validationErrors.state)}
                        helperText={
                          validationErrors.state ||
                          (!profileForm.country
                            ? 'Select country first'
                            : undefined)
                        }
                      >
                        <MenuItem value=''>
                          <em>Select state/province</em>
                        </MenuItem>
                        {stateOptions.map((state) => (
                          <MenuItem key={state.value} value={state.value}>
                            {state.label}
                          </MenuItem>
                        ))}
                      </StyledTextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <StyledTextField
                        fullWidth
                        label='Postal Code'
                        name='postalCode'
                        value={profileForm.postalCode}
                        onChange={handleFormChange}
                        disabled={isReadOnly || !isEditing}
                        error={Boolean(validationErrors.postalCode)}
                        helperText={validationErrors.postalCode}
                        placeholder='Enter postal/ZIP code'
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <StyledTextField
                        select
                        fullWidth
                        label='Country'
                        name='country'
                        value={profileForm.country}
                        onChange={handleFormChange}
                        disabled={isReadOnly || !isEditing}
                        error={Boolean(validationErrors.country)}
                        helperText={validationErrors.country}
                      >
                        <MenuItem value=''>
                          <em>Select country</em>
                        </MenuItem>
                        {COUNTRY_OPTIONS.map((country) => (
                          <MenuItem key={country.code} value={country.code}>
                            {country.name}
                          </MenuItem>
                        ))}
                      </StyledTextField>
                    </Grid>
                  </Grid>

                  {/* Account & Privacy — CRA build delegates the full
                      delete/deactivate flow to the Next.js public page. This
                      keeps the CRA bundle small and avoids maintaining two
                      parallel MUI Dialog implementations. The Next.js page is
                      reachable without auth, so users locked out of CRA can
                      still complete deletion. */}
                  {!isReadOnly && (
                    <Box
                      sx={{
                        mt: 4,
                        pt: 3,
                        borderTop: '1px solid rgba(35,37,54,0.12)',
                      }}
                    >
                      <Typography variant='h6' sx={{ fontWeight: 700, color: '#232536', mb: 1 }}>
                        Account & Privacy
                      </Typography>
                      <Typography sx={{ fontSize: 13.5, color: '#46464c', mb: 2 }}>
                        Deactivate your account (reversible) or schedule
                        permanent deletion (7-day grace) from the unified
                        privacy page.
                      </Typography>
                      <Button
                        variant='outlined'
                        href='https://payments.uaetaxlaws.com/delete-account'
                        target='_blank'
                        rel='noopener noreferrer'
                        sx={{
                          borderColor: '#ba1a1a',
                          color: '#ba1a1a',
                          fontWeight: 700,
                          textTransform: 'none',
                          '&:hover': {
                            borderColor: '#931515',
                            bgcolor: 'rgba(186,26,26,0.05)',
                          },
                        }}
                      >
                        Manage account & privacy
                      </Button>
                    </Box>
                  )}
                </Box>
              )}

              {selectedTab === TAB_KEYS.subscriptions && (
                <SubscriptionsTab
                  subscriptions={subscriptions}
                  loading={subscriptionsLoading}
                  quotaStatus={quotaStatus}
                  onCancelSubscription={handleCancelSubscription}
                  onToggleAutoRenew={handleToggleAutoRenew}
                  autoRenewUpdatingIds={autoRenewUpdatingIds}
                  navigate={navigate}
                  readOnly={isReadOnly}
                />
              )}

              {selectedTab === TAB_KEYS.payments && (
                <PaymentsTab payments={payments} loading={paymentsLoading} />
              )}
            </ResponsiveBox>
          </ResponsivePaper>
        </ResponsiveContainer>

        <Dialog
          open={contentShareDialogOpen}
          onClose={handleCloseContentShareDialog}
          fullWidth
          maxWidth='sm'
        >
          <DialogTitle>Import from another account</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <DialogContentText>
                Enter another user's email address, verify using the one-time
                code sent to that inbox, and import their bookmarks and
                annotations into your account.
              </DialogContentText>

              <TextField
                fullWidth
                label='Source account email'
                type='email'
                value={contentShareSourceEmail}
                onChange={(event) => setContentShareSourceEmail(event.target.value)}
                disabled={
                  contentShareOtpSent ||
                  contentShareSendingOtp ||
                  contentShareVerifyingOtp
                }
                sx={{
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#232536',
                  },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: { xs: 1.5, md: 2 },
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    '&:hover fieldset': {
                      borderColor: '#232536',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#232536',
                    },
                  },
                }}
              />

              {!contentShareOtpSent && (
                <>
                  <Button
                    variant='contained'
                    onClick={() => handleSendContentShareVerificationCode()}
                    disabled={
                      contentShareSendingOtp ||
                      contentShareVerifyingOtp ||
                      contentShareIsSendBlockedByCooldown
                    }
                    sx={{
                      bgcolor: '#232536',
                      color: '#fff',
                      '&:hover': { bgcolor: '#3a3e5a' },
                    }}
                  >
                    {contentShareSendingOtp
                      ? 'Sending...'
                      : contentShareIsSendBlockedByCooldown
                        ? `Wait ${formatCountdown(
                            contentShareResendCooldownSeconds
                          )}`
                        : 'Send Code'}
                  </Button>
                  {contentShareIsSendBlockedByCooldown && (
                    <Typography variant='caption' color='text.secondary'>
                      Please wait{' '}
                      {formatCountdown(contentShareResendCooldownSeconds)} before
                      requesting another code.
                    </Typography>
                  )}
                </>
              )}

              {contentShareOtpSent && (
                <>
                  <Typography variant='body2' color='text.secondary'>
                    Enter the 6-digit code sent to {contentShareActiveMaskedEmail}
                    .
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      width: '100%',
                      justifyContent: 'center',
                      gap: { xs: 0.75, sm: 1.25 },
                    }}
                  >
                    {contentShareOtpDigits.map((digit, index) => (
                      <TextField
                        key={`content-share-otp-${index}`}
                        value={digit}
                        onChange={(event) =>
                          handleContentShareOtpDigitChange(index, event)
                        }
                        onKeyDown={(event) =>
                          handleContentShareOtpDigitKeyDown(index, event)
                        }
                        onPaste={handleContentShareOtpPaste}
                        inputRef={(element) => {
                          contentShareOtpInputRefs.current[index] = element
                        }}
                        disabled={contentShareVerifyingOtp}
                        inputProps={{
                          maxLength: 1,
                          inputMode: 'numeric',
                          pattern: '[0-9]*',
                          style: {
                            textAlign: 'center',
                            fontSize: '1.2rem',
                            fontWeight: 600,
                            padding: '12px 0',
                          },
                        }}
                        sx={{
                          width: { xs: 40, sm: 50 },
                          '& .MuiInputLabel-root.Mui-focused': {
                            color: '#232536',
                          },
                          '& .MuiOutlinedInput-root': {
                            borderRadius: { xs: 1.5, md: 2 },
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                            '&:hover fieldset': {
                              borderColor: '#232536',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#232536',
                            },
                          },
                        }}
                      />
                    ))}
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    {contentShareResendCooldownSeconds > 0 ? (
                      <Typography variant='body2' color='text.secondary'>
                        Resend code in{' '}
                        <Box component='span' sx={{ fontWeight: 700 }}>
                          {formatCountdown(contentShareResendCooldownSeconds)}
                        </Box>
                      </Typography>
                    ) : (
                      <Button
                        variant='text'
                        onClick={() => handleSendContentShareVerificationCode()}
                        disabled={contentShareSendingOtp || contentShareVerifyingOtp}
                      >
                        Resend code
                      </Button>
                    )}
                    <Typography variant='caption' color='text.secondary'>
                      Code expires in {formatCountdown(contentShareOtpExpiresInSeconds)}
                    </Typography>
                  </Box>

                  <Button
                    variant='contained'
                    onClick={handleVerifyContentShareCode}
                    disabled={
                      contentShareVerifyingOtp ||
                      !contentShareIsOtpComplete ||
                      contentShareIsOtpExpired
                    }
                    sx={{
                      bgcolor: '#232536',
                      color: '#fff',
                      '&:hover': { bgcolor: '#3a3e5a' },
                    }}
                  >
                    {contentShareVerifyingOtp
                      ? 'Verifying & Importing...'
                      : 'Verify & Import'}
                  </Button>

                  <Button
                    variant='outlined'
                    onClick={handleRestartContentShareWithNewEmail}
                    disabled={contentShareSendingOtp || contentShareVerifyingOtp}
                    sx={{
                      borderColor: themeColors.primary,
                      color: themeColors.primary,
                      textTransform: 'none',
                      '&:hover': { borderColor: themeColors.hover, color: themeColors.hover },
                    }}
                  >
                    Wrong email? Change it
                  </Button>

                  {contentShareIsOtpExpired && (
                    <Alert severity='warning'>
                      This verification code has expired. Please resend a new
                      code.
                    </Alert>
                  )}
                </>
              )}

              {contentShareMessage && (
                <Alert severity='info'>{contentShareMessage}</Alert>
              )}
              {contentShareError && (
                <Alert severity='error'>{contentShareError}</Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseContentShareDialog}
              disabled={contentShareSendingOtp || contentShareVerifyingOtp}
              variant='outlined'
              sx={{
                borderColor: themeColors.primary,
                color: themeColors.primary,
                textTransform: 'none',
                '&:hover': { borderColor: themeColors.hover, color: themeColors.hover },
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={closeSnackbar}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Alert
            onClose={closeSnackbar}
            severity={snackbar.severity}
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        <Dialog
          open={deleteDraftDialogOpen}
          onClose={handleCloseDeleteDraftDialog}
          fullWidth
          maxWidth='xs'
        >
          <DialogTitle>Delete Draft Blog?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Delete this draft permanently? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseDeleteDraftDialog}
              disabled={blogActionLoadingId === selectedDraftBlogForDelete?.id}
              variant='outlined'
              sx={{
                borderColor: themeColors.primary,
                color: themeColors.primary,
                '&:hover': {
                  borderColor: themeColors.hover,
                  color: themeColors.hover,
                },
              }}
            >
              Keep Draft
            </Button>
            <Button
              variant='contained'
              color='error'
              startIcon={
                blogActionLoadingId === selectedDraftBlogForDelete?.id ? (
                  <CircularProgress size={16} color='inherit' />
                ) : (
                  <DeleteOutline />
                )
              }
              onClick={handleConfirmDeleteDraftBlog}
              disabled={blogActionLoadingId === selectedDraftBlogForDelete?.id}
            >
              {blogActionLoadingId === selectedDraftBlogForDelete?.id
                ? 'Deleting...'
                : 'Confirm Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={cancelDialogOpen}
          onClose={handleCloseCancelDialog}
          fullWidth
          maxWidth='xs'
        >
          <DialogTitle>Cancel Recurring Subscription?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This will stop renewal for{' '}
              {selectedSubscriptionForCancel?.planName || 'this plan'}. You will
              keep access for the already paid period until the current end date.
              No new renewal charge will be attempted after cancellation.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseCancelDialog}
              disabled={cancelSubmitting}
              variant='outlined'
              sx={{
                borderColor: '#232536',
                color: '#232536',          
                '&:hover': {
                  borderColor: '#3a3e5a',
                  color: '#3a3e5a',
                },
              }}
            >
              Keep Plan
            </Button>
            <Button
              variant='contained'
              color='error'
              startIcon={
                cancelSubmitting ? (
                  <CircularProgress size={16} color='inherit' />
                ) : (
                  <Cancel />
                )
              }
              onClick={handleConfirmCancelSubscription}
              disabled={cancelSubmitting}
            >
              {cancelSubmitting ? 'Cancelling...' : 'Confirm Cancel'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  )
}

const PaymentsTab = ({ payments, loading }) => {
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [durationFilter, setDurationFilter] = useState('all')
  const [scopeFilter, setScopeFilter] = useState('all')
  const [countryFilter, setCountryFilter] = useState('all')
  const [paymentsPage, setPaymentsPage] = useState(1)
  const [paymentsItemsPerPage, setPaymentsItemsPerPage] = useState(10)

  const formatDateTime = (dateInput) => {
    const date = convertArrayToDate(dateInput)
    if (!date) {
      return 'N/A'
    }
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatCurrency = (amount, currency = 'AED') => {
    const safeAmount = Number(amount) || 0
    const normalizedCurrency = (currency || 'AED').toUpperCase()
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: normalizedCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(safeAmount)
  }

  const getPaymentDate = (payment) =>
    convertArrayToDate(payment?.completedAt || payment?.paymentDate)

  const sortedPayments = useMemo(
    () =>
      [...payments].sort(
        (left, right) =>
          getPaymentSortTimestamp(right) - getPaymentSortTimestamp(left)
      ),
    [payments]
  )

  const paymentsWithPlanMeta = useMemo(
    () =>
      sortedPayments.map((payment) => ({
        payment,
        planMeta: resolvePaymentPlanMeta(payment),
      })),
    [sortedPayments]
  )

  const preCountryFilteredPayments = useMemo(() => {
    const fromDate = dateFrom ? new Date(`${dateFrom}T00:00:00`) : null
    const toDate = dateTo ? new Date(`${dateTo}T23:59:59.999`) : null

    return paymentsWithPlanMeta.filter(({ payment, planMeta }) => {
      const paymentDate = getPaymentDate(payment)
      if (!paymentDate) {
        return false
      }
      if (fromDate && paymentDate < fromDate) {
        return false
      }
      if (toDate && paymentDate > toDate) {
        return false
      }

      if (durationFilter !== 'all' && planMeta.duration !== durationFilter) {
        return false
      }

      if (scopeFilter !== 'all' && planMeta.scope !== scopeFilter) {
        return false
      }

      return true
    })
  }, [dateFrom, dateTo, durationFilter, paymentsWithPlanMeta, scopeFilter])

  const availableCountryFilters = useMemo(() => {
    const countrySet = new Set()
    preCountryFilteredPayments.forEach(({ planMeta }) => {
      if (planMeta.scope === 'country' && planMeta.country) {
        countrySet.add(planMeta.country)
      }
    })
    return PAYMENT_COUNTRY_FILTER_ORDER.filter((country) =>
      countrySet.has(country)
    )
  }, [preCountryFilteredPayments])

  const filteredPayments = useMemo(() => {
    const narrowedPayments =
      scopeFilter === 'country' && countryFilter !== 'all'
        ? preCountryFilteredPayments.filter(
            ({ planMeta }) => planMeta.country === countryFilter
          )
        : preCountryFilteredPayments

    return narrowedPayments.map(({ payment }) => payment)
  }, [countryFilter, preCountryFilteredPayments, scopeFilter])

  const totalPaid = useMemo(
    () =>
      filteredPayments.reduce(
        (sum, payment) => sum + (Number(payment?.finalAmount) || 0),
        0
      ),
    [filteredPayments]
  )

  const totalPaymentPages = useMemo(
    () => Math.ceil(filteredPayments.length / paymentsItemsPerPage),
    [filteredPayments.length, paymentsItemsPerPage]
  )

  const paginatedPayments = useMemo(() => {
    const start = (paymentsPage - 1) * paymentsItemsPerPage
    return filteredPayments.slice(start, start + paymentsItemsPerPage)
  }, [filteredPayments, paymentsItemsPerPage, paymentsPage])

  const showPaymentsPagination = useMemo(
    () => filteredPayments.length > paymentsItemsPerPage && totalPaymentPages > 1,
    [filteredPayments.length, paymentsItemsPerPage, totalPaymentPages]
  )

  useEffect(() => {
    setPaymentsPage(1)
  }, [
    countryFilter,
    dateFrom,
    dateTo,
    durationFilter,
    paymentsItemsPerPage,
    scopeFilter,
  ])

  useEffect(() => {
    if (paymentsPage > totalPaymentPages && totalPaymentPages > 0) {
      setPaymentsPage(totalPaymentPages)
    }
  }, [paymentsPage, totalPaymentPages])

  useEffect(() => {
    if (scopeFilter !== 'country') {
      if (countryFilter !== 'all') {
        setCountryFilter('all')
      }
      return
    }

    if (
      countryFilter !== 'all' &&
      !availableCountryFilters.includes(countryFilter)
    ) {
      setCountryFilter('all')
    }
  }, [availableCountryFilters, countryFilter, scopeFilter])

  const handlePaymentsItemsPerPageChange = useCallback(
    (nextItemsPerPage) => {
      setPaymentsItemsPerPage(nextItemsPerPage)
      setPaymentsPage(1)
    },
    []
  )

  const getPaymentLabel = (payment) => {
    if ((payment?.paymentType || '').toUpperCase() === 'CREDITS') {
      return `${payment?.creditsPurchased || 0} credits`
    }
    return payment?.planName || 'Subscription purchase'
  }

  const handleClearAllFilters = useCallback(() => {
    setDateFrom('')
    setDateTo('')
    setDurationFilter('all')
    setScopeFilter('all')
    setCountryFilter('all')
    setPaymentsPage(1)
  }, [])

  const hasActiveFilters =
    Boolean(dateFrom) ||
    Boolean(dateTo) ||
    durationFilter !== 'all' ||
    scopeFilter !== 'all' ||
    countryFilter !== 'all'

  const formatFilterDateLabel = useCallback((rawDate) => {
    if (!rawDate) {
      return ''
    }

    const parsedDate = new Date(`${rawDate}T00:00:00`)
    if (Number.isNaN(parsedDate.getTime())) {
      return rawDate
    }

    return parsedDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }, [])

  const activeFilterSummaryChips = useMemo(() => {
    const chips = []

    if (dateFrom) {
      chips.push(`From: ${formatFilterDateLabel(dateFrom)}`)
    }

    if (dateTo) {
      chips.push(`To: ${formatFilterDateLabel(dateTo)}`)
    }

    if (durationFilter !== 'all') {
      chips.push(
        `Duration: ${durationFilter === 'monthly' ? 'Monthly' : 'Daily'}`
      )
    }

    if (scopeFilter !== 'all') {
      chips.push(`Scope: ${scopeFilter === 'gcc' ? 'GCC' : 'Country'}`)
    }

    if (scopeFilter === 'country' && countryFilter !== 'all') {
      chips.push(`Country: ${countryFilter}`)
    }

    return chips
  }, [
    countryFilter,
    dateFrom,
    dateTo,
    durationFilter,
    formatFilterDateLabel,
    scopeFilter,
  ])

  const filterToggleGroupSx = {
    width: { xs: '100%', sm: 'auto' },
    '& .MuiToggleButton-root': {
      borderColor: '#d1d5db',
      color: themeColors.primary,
      textTransform: 'none',
      fontSize: '0.75rem',
      px: 1.4,
      '&:hover': {
        borderColor: themeColors.hover,
        color: themeColors.hover,
        backgroundColor: `${themeColors.secondary}22`,
      },
      '&.Mui-selected': {
        borderColor: themeColors.primary,
        color: themeColors.primary,
        backgroundColor: themeColors.secondary,
        fontWeight: 600,
      },
      '&.Mui-selected:hover': {
        borderColor: themeColors.hover,
        color: themeColors.hover,
        backgroundColor: themeColors.secondary,
      },
    },
  }

  const paymentsDateFieldSx = {
    minWidth: { xs: '100%', sm: 210 },
    '& .MuiInputLabel-root': {
      color: '#64748b',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: themeColors.primary,
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: '#cbd5e1',
      },
      '&:hover fieldset': {
        borderColor: themeColors.primary,
      },
      '&.Mui-focused fieldset': {
        borderColor: themeColors.primary,
      },
    },
    '& .MuiSvgIcon-root': {
      color: themeColors.primary,
    },
  }

  const paymentsDatePickerPopperSx = {
    '& .MuiPaper-root': {
      borderRadius: 2,
      border: `1px solid ${themeColors.secondary}`,
      boxShadow: '0 10px 26px rgba(35, 37, 54, 0.16)',
    },
    '& .MuiPickersCalendarHeader-label': {
      color: themeColors.primary,
      fontWeight: 700,
    },
    '& .MuiDayCalendar-weekDayLabel': {
      color: themeColors.primary,
      fontWeight: 600,
    },
    '& .MuiPickersArrowSwitcher-button, & .MuiPickersCalendarHeader-switchViewButton': {
      color: themeColors.primary,
    },
    '& .MuiPickersDay-root': {
      color: themeColors.primary,
      fontWeight: 500,
    },
    '& .MuiPickersDay-root:hover': {
      backgroundColor: 'rgba(255, 207, 81, 0.2)',
    },
    '& .MuiPickersDay-root.Mui-selected': {
      backgroundColor: themeColors.secondary,
      color: themeColors.primary,
      fontWeight: 700,
    },
    '& .MuiPickersDay-root.Mui-selected:hover': {
      backgroundColor: themeColors.secondary,
    },
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Paper
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            border: '1px solid #e2e8f0',
            backgroundColor: '#f8fafc',
          }}
        >
          <Typography variant='body2' color='text.secondary'>
            Total Paid to GTL
          </Typography>
          <Typography
            variant='h5'
            sx={{ fontWeight: 700, color: themeColors.primary }}
          >
            {formatCurrency(totalPaid)}
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            {hasActiveFilters
              ? `Filtered results: ${filteredPayments.length} payment(s)`
              : 'Completed payments shown newest first'}
          </Typography>
        </Paper>

        <Paper
          sx={{
            p: { xs: 2, md: 2.5 },
            mb: 2,
            borderRadius: 2,
            border: '1px solid #d9e2ec',
            boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)',
          }}
        >
          <Stack spacing={{ xs: 2, md: 2.25 }}>
          <Stack
            direction={{ xs: 'column', xl: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', xl: 'center' }}
            justifyContent='space-between'
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', md: 'center' }}
              useFlexGap
              flexWrap='wrap'
            >
              <DatePicker
                label='From'
                format='DD-MM-YYYY'
                value={dateFrom ? dayjs(dateFrom) : null}
                onChange={(value) =>
                  setDateFrom(
                    value && value.isValid() ? value.format('YYYY-MM-DD') : ''
                  )
                }
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: paymentsDateFieldSx,
                  },
                  popper: {
                    sx: paymentsDatePickerPopperSx,
                  },
                }}
              />
              <DatePicker
                label='To'
                format='DD-MM-YYYY'
                value={dateTo ? dayjs(dateTo) : null}
                onChange={(value) =>
                  setDateTo(
                    value && value.isValid() ? value.format('YYYY-MM-DD') : ''
                  )
                }
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: paymentsDateFieldSx,
                  },
                  popper: {
                    sx: paymentsDatePickerPopperSx,
                  },
                }}
              />
              {hasActiveFilters && (
                <Button
                  variant='outlined'
                  size='small'
                  onClick={handleClearAllFilters}
                  sx={{
                    minHeight: 40,
                    px: 2,
                    borderColor: themeColors.primary,
                    color: themeColors.primary,
                    borderRadius: 1.5,
                    fontWeight: 500,
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: themeColors.hover,
                      color: themeColors.hover,
                      backgroundColor: 'rgba(255, 207, 81, 0.24)',
                    },
                  }}
                >
                  Clear filters
                </Button>
              )}
            </Stack>

            <Box sx={{ alignSelf: { xs: 'flex-start', xl: 'center' } }}>
              <ItemsPerPageSelector
                itemsPerPage={paymentsItemsPerPage}
                setItemsPerPage={handlePaymentsItemsPerPageChange}
                loading={loading}
                options={[5, 10, 20, 30]}
              />
            </Box>
          </Stack>

          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              spacing={{ xs: 1.75, lg: 3 }}
            >
              <Box sx={{ minWidth: { xs: '100%', md: 300, lg: 320 } }}>
                <Typography
                  variant='subtitle2'
                  sx={{ color: themeColors.primary, fontWeight: 700, mb: 0.9 }}
                >
                  Duration
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  size='small'
                  value={durationFilter}
                  onChange={(_, nextDuration) => {
                    if (nextDuration) {
                      setDurationFilter(nextDuration)
                    }
                  }}
                  sx={{
                    ...filterToggleGroupSx,
                    '& .MuiToggleButton-root': {
                      ...filterToggleGroupSx['& .MuiToggleButton-root'],
                      minHeight: 42,
                      flex: { xs: 1, sm: 'initial' },
                    },
                  }}
                >
                  <ToggleButton value='all'>All</ToggleButton>
                  <ToggleButton value='monthly'>Monthly</ToggleButton>
                  <ToggleButton value='daily'>Daily</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <Box sx={{ minWidth: { xs: '100%', md: 300, lg: 320 } }}>
                <Typography
                  variant='subtitle2'
                  sx={{ color: themeColors.primary, fontWeight: 700, mb: 0.9 }}
                >
                  Scope
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  size='small'
                  value={scopeFilter}
                  onChange={(_, nextScope) => {
                    if (nextScope) {
                      setScopeFilter(nextScope)
                    }
                  }}
                  sx={{
                    ...filterToggleGroupSx,
                    '& .MuiToggleButton-root': {
                      ...filterToggleGroupSx['& .MuiToggleButton-root'],
                      minHeight: 42,
                      flex: { xs: 1, sm: 'initial' },
                    },
                  }}
                >
                  <ToggleButton value='all'>All</ToggleButton>
                  <ToggleButton value='gcc'>GCC</ToggleButton>
                  <ToggleButton value='country'>Country</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Stack>

            {scopeFilter === 'country' && (
              <Box sx={{ pt: 0.25 }}>
                <Typography
                  variant='subtitle2'
                  sx={{ color: themeColors.primary, fontWeight: 700, mb: 1.1 }}
                >
                  Country
                </Typography>
                {availableCountryFilters.length === 0 ? (
                  <Typography variant='body2' color='text.secondary'>
                    No country-specific payments in this selection.
                  </Typography>
                ) : (
                  <Stack
                    direction='row'
                    spacing={1}
                    useFlexGap
                    flexWrap='wrap'
                    sx={{ rowGap: 1 }}
                  >
                    <Chip
                      clickable
                      label='All countries'
                      onClick={() => setCountryFilter('all')}
                      variant={countryFilter === 'all' ? 'filled' : 'outlined'}
                      sx={{
                        minHeight: 40,
                        px: 0.75,
                        borderColor: themeColors.primary,
                        color: themeColors.primary,
                        backgroundColor:
                          countryFilter === 'all'
                            ? themeColors.secondary
                            : 'transparent',
                        fontWeight: countryFilter === 'all' ? 600 : 500,
                        '&:hover': {
                          borderColor: themeColors.hover,
                          color: themeColors.hover,
                        },
                      }}
                    />
                    {availableCountryFilters.map((country) => (
                      <Chip
                        key={country}
                        clickable
                        label={country}
                        onClick={() => setCountryFilter(country)}
                        variant={
                          countryFilter === country ? 'filled' : 'outlined'
                        }
                        sx={{
                          minHeight: 40,
                          px: 0.75,
                          borderColor: themeColors.primary,
                          color: themeColors.primary,
                          backgroundColor:
                            countryFilter === country
                              ? themeColors.secondary
                              : 'transparent',
                          fontWeight: countryFilter === country ? 600 : 500,
                          '&:hover': {
                            borderColor: themeColors.hover,
                            color: themeColors.hover,
                          },
                        }}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            )}
          </Stack>
          </Stack>
        </Paper>

        {hasActiveFilters && activeFilterSummaryChips.length > 0 && (
          <Paper
            sx={{
              p: { xs: 1.5, md: 1.75 },
              mb: 2.25,
              borderRadius: 2,
              border: '1px dashed #d1d5db',
              backgroundColor: '#ffffff',
              boxShadow: '0 2px 8px rgba(15, 23, 42, 0.05)',
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
            >
              <Typography
                variant='subtitle2'
                sx={{
                  color: themeColors.primary,
                  fontWeight: 700,
                  lineHeight: 1.3,
                }}
              >
                Active filters
              </Typography>
              <Stack
                direction='row'
                spacing={1}
                useFlexGap
                flexWrap='wrap'
                sx={{ rowGap: 0.75 }}
              >
                {activeFilterSummaryChips.map((label) => (
                  <Chip
                    key={label}
                    size='small'
                    label={label}
                    variant='outlined'
                    sx={{
                      minHeight: 34,
                      borderColor: themeColors.primary,
                      color: themeColors.primary,
                      backgroundColor: `${themeColors.secondary}24`,
                      fontWeight: 600,
                    }}
                  />
                ))}
              </Stack>
            </Stack>
          </Paper>
        )}

      {sortedPayments.length === 0 ? (
        <Paper
          sx={{
            p: 4,
            textAlign: 'center',
            bgcolor: '#f9f9f9',
            borderRadius: 2,
          }}
        >
          <PaymentsIcon sx={{ fontSize: 52, color: '#d1d5db', mb: 1 }} />
          <Typography variant='h6'>No payments yet</Typography>
          <Typography variant='body2' color='text.secondary'>
            Your completed payments will appear here.
          </Typography>
        </Paper>
      ) : filteredPayments.length === 0 ? (
        <Paper
          sx={{
            p: 4,
            textAlign: 'center',
            bgcolor: '#f9f9f9',
            borderRadius: 2,
          }}
        >
          <PaymentsIcon sx={{ fontSize: 52, color: '#d1d5db', mb: 1 }} />
          <Typography variant='h6'>No payments match these filters</Typography>
          <Typography variant='body2' color='text.secondary'>
            Try broadening or clearing your payment filters.
          </Typography>
        </Paper>
      ) : (
        <>
          <Stack spacing={1.5}>
            {paginatedPayments.map((payment) => (
              <Paper
                key={payment.id || `${payment.paymentDate}-${payment.finalAmount}`}
                variant='outlined'
                sx={{ p: 2, borderColor: '#e2e8f0' }}
              >
                <Grid container spacing={1.5} alignItems='center'>
                  <Grid item xs={12} md={7}>
                    <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
                      {getPaymentLabel(payment)}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      {formatDateTime(payment.completedAt || payment.paymentDate)}
                    </Typography>
                    {/* <Typography variant='caption' color='text.secondary'>
                      {payment?.paymentGateway || 'Gateway N/A'}
                    </Typography> */}
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'row', md: 'column' },
                        justifyContent: { xs: 'space-between', md: 'center' },
                        alignItems: { xs: 'center', md: 'flex-end' },
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant='h6'
                        sx={{ fontWeight: 700, color: themeColors.primary }}
                      >
                        {formatCurrency(payment?.finalAmount, payment?.currency)}
                      </Typography>
                      <Chip
                        size='small'
                        label={payment?.displayStatus || 'Completed'}
                        sx={{
                          backgroundColor: '#e8f5e9',
                          color: '#166534',
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Stack>
          {showPaymentsPagination && (
            <MemoizedPagination
              itemsPerPage={paymentsItemsPerPage}
              totalItems={filteredPayments.length}
              currentPage={paymentsPage}
              paginate={setPaymentsPage}
            />
          )}
        </>
      )}
      </Box>
    </LocalizationProvider>
  )
}

const SubscriptionCard = ({
  subscription,
  borderColor,
  statusChipColor,
  statusDetail,
  startLabel,
  endLabel,
  showDaysRemaining,
  showAutoRenewAction,
  showCancelAction,
  isAutoRenewUpdating,
  onToggleAutoRenew,
  onCancelSubscription,
}) => (
  <Paper
    sx={{
      p: 3,
      borderRadius: 2,
      border: borderColor,
    }}
  >
    <Grid container spacing={2} alignItems='center'>
      <Grid item xs={12} md={6}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <CardMembership sx={{ mr: 1, color: '#43af76' }} />
          <Typography variant='h6' sx={{ fontWeight: 600 }}>
            {subscription.planName}
          </Typography>
        </Box>

        {statusDetail && (
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
            {statusDetail}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label={subscription.status}
            size='small'
            color={statusChipColor}
            sx={{ fontWeight: 600 }}
          />
          <Chip
            label={subscription.planCode}
            size='small'
            variant='outlined'
          />
        </Box>
      </Grid>

      <Grid item xs={12} md={4}>
        <Typography variant='body2' color='text.secondary'>
          <strong>{startLabel}</strong> {formatSubscriptionDate(subscription.startDate)}
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          <strong>{endLabel}</strong> {formatSubscriptionDate(subscription.endDate)}
        </Typography>
        {showDaysRemaining && (
          <Typography variant='body2' color='text.secondary'>
            <strong>Days remaining:</strong> {subscription.daysRemaining}
          </Typography>
        )}
        <Typography
          variant='h6'
          sx={{ fontWeight: 700, color: themeColors.primary, mt: 1 }}
        >
          {formatSubscriptionPrice(subscription.amountPaid)}
          {subscription.isRecurring && (
            <Typography
              component='span'
              variant='body2'
              sx={{ color: 'text.secondary', ml: 0.5 }}
            >
              / {formatSubscriptionDurationType(subscription.durationType)}
            </Typography>
          )}
        </Typography>
      </Grid>

      <Grid item xs={12} md={2}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {showAutoRenewAction && (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: { xs: 'flex-start', md: 'flex-end' },
                gap: 0.5,
                border: `1px solid ${themeColors.primary}`,
                borderRadius: 1.25,
                px: 1,
                py: 0.5,
                bgcolor: subscription.autoRenew
                  ? `${themeColors.primary}12`
                  : '#ffffff',
              }}
            >
              <Typography
                variant='caption'
                sx={{ fontWeight: 700, color: themeColors.primary }}
              >
                Auto-renew
              </Typography>
              <Switch
                size='small'
                checked={Boolean(subscription.autoRenew)}
                disabled={isAutoRenewUpdating}
                onChange={() =>
                  onToggleAutoRenew(subscription.id, subscription.autoRenew)
                }
                sx={{
                  ml: 0.25,
                  mr: 0.25,
                  '& .MuiSwitch-switchBase': {
                    color: '#c8cfdb',
                    '&:hover': {
                      backgroundColor: `${themeColors.primary}12`,
                    },
                  },
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#ffffff',
                    '&:hover': {
                      backgroundColor: `${themeColors.primary}1f`,
                    },
                    '& + .MuiSwitch-track': {
                      backgroundColor: themeColors.primary,
                      opacity: 1,
                    },
                  },
                  '& .MuiSwitch-track': {
                    backgroundColor: '#d4dae5',
                    opacity: 1,
                  },
                }}
              />
            </Box>
          )}
          {showCancelAction && (
            <Button
              variant='outlined'
              size='small'
              color='error'
              startIcon={<Cancel />}
              onClick={() => onCancelSubscription(subscription)}
              sx={{ fontSize: '0.75rem' }}
            >
              Cancel
            </Button>
          )}
        </Box>
      </Grid>
    </Grid>

    {Array.isArray(subscription.countries) &&
      subscription.countries.length > 0 && (
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
          <Typography variant='body2' sx={{ fontWeight: 600, mb: 1 }}>
            Countries Covered:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {subscription.countries.includes('ALL') ? (
              <Chip
                label='All GCC Countries'
                size='small'
                sx={{ bgcolor: '#e8f5e9', fontWeight: 600 }}
              />
            ) : (
              subscription.countries.map((country) => (
                <Chip
                  key={country}
                  label={country}
                  size='small'
                  sx={{ bgcolor: '#e8f5e9' }}
                />
              ))
            )}
          </Box>
        </Box>
      )}
  </Paper>
)

const MemoizedSubscriptionCard = React.memo(SubscriptionCard)

const SubscriptionsTab = ({
  subscriptions,
  loading,
  quotaStatus,
  onCancelSubscription,
  onToggleAutoRenew,
  autoRenewUpdatingIds,
  navigate,
  readOnly = false,
}) => {

  const isCancelledScheduled = useCallback((subscription) => {
    if (!subscription || subscription.status !== 'Cancelled') {
      return false
    }
    const startDate = convertArrayToDate(subscription.startDate)
    return Boolean(startDate && startDate.getTime() > Date.now())
  }, [])

  const cancelledButScheduledSubscriptions = useMemo(
    () =>
      subscriptions.filter(
        (sub) =>
          sub.status === 'Cancelled' &&
          sub.isActive &&
          !sub.isExpired &&
          isCancelledScheduled(sub)
      ),
    [subscriptions, isCancelledScheduled]
  )

  const cancelledButActiveSubscriptions = useMemo(
    () =>
      subscriptions.filter(
        (sub) =>
          sub.status === 'Cancelled' &&
          sub.isActive &&
          !sub.isExpired &&
          !isCancelledScheduled(sub)
      ),
    [subscriptions, isCancelledScheduled]
  )

  const activeSubscriptions = useMemo(
    () =>
      subscriptions.filter(
        (sub) =>
          sub.status === 'Active' &&
          !cancelledButActiveSubscriptions.some((cancelled) => cancelled.id === sub.id)
      ),
    [subscriptions, cancelledButActiveSubscriptions]
  )

  const upcomingSubscriptions = useMemo(
    () => subscriptions.filter((sub) => sub.status === 'Scheduled'),
    [subscriptions]
  )

  const expiredOrEndedSubscriptions = useMemo(
    () =>
      subscriptions.filter(
        (sub) =>
          sub.status === 'Expired' ||
          (sub.status === 'Cancelled' && (!sub.isActive || sub.isExpired)) ||
          (!['Active', 'Cancelled', 'Scheduled'].includes(sub.status || ''))
      ),
    [subscriptions]
  )

  const hasAnyActiveScheduledOrCancelledActivePlan = useMemo(
    () =>
      activeSubscriptions.length > 0 ||
      upcomingSubscriptions.length > 0 ||
      cancelledButActiveSubscriptions.length > 0 ||
      cancelledButScheduledSubscriptions.length > 0,
    [
      activeSubscriptions.length,
      upcomingSubscriptions.length,
      cancelledButActiveSubscriptions.length,
      cancelledButScheduledSubscriptions.length,
    ]
  )

  const [isQuotaAccordionExpanded, setIsQuotaAccordionExpanded] =
    useState(true)

  useEffect(() => {
    if (loading) {
      return
    }
    setIsQuotaAccordionExpanded(!hasAnyActiveScheduledOrCancelledActivePlan)
  }, [loading, hasAnyActiveScheduledOrCancelledActivePlan])

  const [expandedSubscriptionAccordions, setExpandedSubscriptionAccordions] =
    useState({
      active: true,
      cancelled: false,
      expired: false,
    })

  const handleSubscriptionAccordionChange = useCallback(
    (section) => (_, isExpanded) => {
      setExpandedSubscriptionAccordions((prev) => ({
        ...prev,
        [section]: isExpanded,
      }))
    },
    []
  )

  const canShowAutoRenewAction = useCallback(
    (subscription) => {
      if (readOnly || !subscription?.isRecurring) {
        return false
      }

      if (subscription.canToggleAutoRenew) {
        return true
      }

      // Fallback: allow disabling auto-renew for upcoming plans even if API
      // capability flags are missing from stale data.
      return (
        subscription.autoRenew &&
        subscription.status === 'Scheduled' &&
        subscription.isActive &&
        !subscription.isExpired &&
        subscription.status !== 'Cancelled'
      )
    },
    [readOnly]
  )

  const canShowCancelAction = useCallback(
    (subscription) => {
      if (readOnly || !subscription?.isRecurring) {
        return false
      }

      if (subscription.canCancel) {
        return true
      }

      // Fallback for upcoming recurring plans when capability flags lag.
      return (
        subscription.status === 'Scheduled' &&
        subscription.isActive &&
        !subscription.isExpired &&
        subscription.status !== 'Cancelled'
      )
    },
    [readOnly]
  )

  const renderSubscriptionGrid = (rows) => (
    <Grid container spacing={2}>
      {rows.map((subscription) => {
        const isCancelledWithAccess =
          subscription.status === 'Cancelled' &&
          subscription.isActive &&
          !subscription.isExpired
        const statusDetail = isCancelledWithAccess
          ? isCancelledScheduled(subscription)
            ? `Cancelled - queued access window starts on ${formatSubscriptionDate(
                subscription.startDate
              )} and ends on ${formatSubscriptionDate(subscription.endDate)}`
            : `Cancelled - access remains until ${formatSubscriptionDate(
                subscription.endDate
              )}`
          : subscription.expiryMessage || ''

        return (
          <Grid item xs={12} key={subscription.id}>
            <MemoizedSubscriptionCard
              subscription={subscription}
              borderColor={getSubscriptionBorderColor(subscription.status)}
              statusChipColor={getSubscriptionStatusChipColor(
                subscription.status
              )}
              statusDetail={statusDetail}
              startLabel={
                subscription.status === 'Scheduled' ? 'Starts:' : 'Started:'
              }
              endLabel={
                subscription.status === 'Scheduled'
                  ? 'Will end:'
                  : subscription.isExpired
                    ? 'Expired:'
                    : 'Expires:'
              }
              showDaysRemaining={
                subscription.daysRemaining !== undefined &&
                ['Active', 'Scheduled'].includes(subscription.status)
              }
              showAutoRenewAction={canShowAutoRenewAction(subscription)}
              showCancelAction={canShowCancelAction(subscription)}
              isAutoRenewUpdating={autoRenewUpdatingIds?.has(subscription.id)}
              onToggleAutoRenew={onToggleAutoRenew}
              onCancelSubscription={onCancelSubscription}
            />
          </Grid>
        )
      })}
    </Grid>
  )

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      {!readOnly && (
        <Alert severity='info' sx={{ mb: 3 }}>
          Enable Auto-Renew keeps recurring plans active by charging your saved
          payment method at renewal time. Disable it if you want the plan to end
          on its expiry date.
        </Alert>
      )}

      {quotaStatus && (
        <Accordion
          expanded={isQuotaAccordionExpanded}
          onChange={(_, isExpanded) => setIsQuotaAccordionExpanded(isExpanded)}
          sx={{
            mb: 3,
            bgcolor: '#f9f9f9',
            borderRadius: 2,
            border: '1px solid #e0e0e0',
            boxShadow: 'none',
            overflow: 'hidden',
            '&::before': {
              display: 'none',
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              px: 3,
              '& .MuiAccordionSummary-content': {
                my: 2,
              },
            }}
          >
            <Typography variant='subtitle1' sx={{ fontWeight: 700, color: 'text.primary' }}>
              Current usage and quota for Free Tier
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 3, pb: 3, pt: 0 }}>
            <Grid container spacing={3} justifyContent='flex-start'>
              {[
                {
                  value: quotaStatus.remainingLinkClicks ?? 0,
                  label: 'Link clicks left',
                  caption: `of ${quotaStatus.monthlyLinkClicksLimit ?? 0} free monthly`,
                  color: '#232536', // #43af76
                },
                {
                  value: quotaStatus.remainingDtaaViews ?? 0,
                  label: 'DTAA views left',
                  caption: `of ${quotaStatus.monthlyDtaaViewsLimit ?? 0} free monthly`,
                  color: '#232536', // #ff9800'
                },
                {
                  value: quotaStatus.activeSubscriptions?.length ?? 0,
                  label: 'Active plans',
                  caption: 'Current active subscriptions', // Added a caption for height symmetry
                  color: '#232536', // #9c27b0
                },
              ].map((stat, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Box
                    sx={{
                      textAlign: 'center',
                      p: 3,
                      bgcolor: 'white',
                      borderRadius: 2,
                      border: '1px solid #efefef',
                      height: '100%', // Makes all boxes the same height
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                    }}
                  >
                    <Typography
                      variant='h4'
                      sx={{ fontWeight: 800, color: stat.color, mb: 0.5 }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{ fontWeight: 500, color: 'text.primary' }}
                    >
                      {stat.label}
                    </Typography>
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{ mt: 0.5 }}
                    >
                      {stat.caption}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {quotaStatus.quotaResetDate && (
              <Box
                sx={{
                  mt: 3,
                  pt: 2,
                  textAlign: 'center',
                  borderTop: '1px solid #eee',
                }}
              >
                <Typography variant='body2' color='text.secondary'>
                  Free quota resets on:{' '}
                  <strong style={{ color: '#333' }}>
                    {formatSubscriptionDate(quotaStatus.quotaResetDate)}
                  </strong>
                </Typography>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      )}
      <Typography variant='h6' sx={{ fontWeight: 600, mb: 2 }}>
        Total Subscriptions ({subscriptions.length})
      </Typography>

      {subscriptions.length === 0 ? (
        <Paper
          sx={{
            p: 4,
            textAlign: 'center',
            bgcolor: '#f9f9f9',
            borderRadius: 2,
          }}
        >
          <CardMembership sx={{ fontSize: 60, color: '#e0e0e0', mb: 2 }} />
          <Typography variant='h6' sx={{ mb: 1 }}>
            No Subscriptions
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
            Upgrade to a subscription plan for unlimited access to documents
          </Typography>
          {/* <Button
            variant='contained'
            startIcon={<TrendingUp />}
            onClick={() => navigate('/pricing')}
            sx={{
              bgcolor: '#232536',
              color: '#ffcf51',
              '&:hover': { bgcolor: '#3a3e5a' },
            }}
          >
            View Plans
          </Button> */}
        </Paper>
      ) : (
        <Box>
          <Accordion
            expanded={expandedSubscriptionAccordions.active}
            onChange={handleSubscriptionAccordionChange('active')}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
                Active Subscriptions ({activeSubscriptions.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              {activeSubscriptions.length > 0 ? (
                renderSubscriptionGrid(activeSubscriptions)
              ) : (
                <Typography variant='body2' color='text.secondary'>
                  No active subscriptions.
                </Typography>
              )}

              {upcomingSubscriptions.length > 0 && (
                <Box sx={{ mt: activeSubscriptions.length > 0 ? 3 : 1 }}>
                  <Typography
                    variant='subtitle2'
                    sx={{ fontWeight: 700, mb: 1.5 }}
                  >
                    Upcoming Subscriptions ({upcomingSubscriptions.length})
                  </Typography>
                  {renderSubscriptionGrid(upcomingSubscriptions)}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expandedSubscriptionAccordions.cancelled}
            onChange={handleSubscriptionAccordionChange('cancelled')}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
                Cancelled Subscriptions (
                {cancelledButActiveSubscriptions.length +
                  cancelledButScheduledSubscriptions.length}
                )
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              {cancelledButActiveSubscriptions.length > 0 ||
              cancelledButScheduledSubscriptions.length > 0 ? (
                <>
                  {cancelledButActiveSubscriptions.length > 0 && (
                    <>
                      <Alert severity='warning' sx={{ mb: 1.5 }}>
                        Cancelled plans below remain active until their end
                        date.
                      </Alert>
                      {renderSubscriptionGrid(cancelledButActiveSubscriptions)}
                    </>
                  )}

                  {cancelledButScheduledSubscriptions.length > 0 && (
                    <Box sx={{ mt: cancelledButActiveSubscriptions.length > 0 ? 3 : 0 }}>
                      <Alert severity='info' sx={{ mb: 1.5 }}>
                        Cancelled queued plans below are still scheduled for
                        their paid period but will not auto-renew.
                      </Alert>
                      {renderSubscriptionGrid(cancelledButScheduledSubscriptions)}
                    </Box>
                  )}
                </>
              ) : (
                <Typography variant='body2' color='text.secondary'>
                  No cancelled subscriptions with pending access.
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expandedSubscriptionAccordions.expired}
            onChange={handleSubscriptionAccordionChange('expired')}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
                Expired/Ended Subscriptions (
                {expiredOrEndedSubscriptions.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              {expiredOrEndedSubscriptions.length > 0 ? (
                renderSubscriptionGrid(expiredOrEndedSubscriptions)
              ) : (
                <Typography variant='body2' color='text.secondary'>
                  No expired or ended subscriptions.
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        </Box>
      )}

      {!readOnly && (
        <Box
          sx={{
            mt: 4,
            p: 3,
            bgcolor: '#f5f5f5',
            borderRadius: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box>
            <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 0.5 }}>
              Need more access?
            </Typography>
            {/*<Typography variant='body2' color='text.secondary'>
              Upgrade your plan
            </Typography>*/}
          </Box>
          <Button
            variant='contained'
            startIcon={<TrendingUp />}
            onClick={() => navigate('/pricing')}
            sx={{
              // fontWeight: 600,
              bgcolor: '#232536',
              color: '#ffffff',
              '&:hover': { bgcolor: '#3a3e5a' },
            }}
          >
            View Plans
          </Button>
        </Box>
      )}
    </Box>
  )
}

export default Profile

